import { useEffect, useState, useMemo } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, updateDoc, doc, getDoc, deleteDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useGlobalCategories } from "../hooks/useGlobalCategories";
import { useObjectCategories } from "../hooks/useObjectCategories";
import { useLabelAttributes } from "../hooks/useLabelAttributes";
import LabelListPanel from "../components/LabelListPanel";
import AttributePanel from "../components/AttributePanel";
import { LabelAttribute } from "../types/labelAttribute";
import { 
  getRequiredSteps, 
  getLabelsForStep, 
  stepKeyFromMedia,
  stepKeyToRole,
  getStepDisplayLabel,
  type LabelData
} from "../utils/submissionHelpers";

// ─────────────────────────────────────────────────────────────────────────────
// Multi-photo types and helpers
// ─────────────────────────────────────────────────────────────────────────────
type MediaRole = 'primary' | 'expiration_label' | 'after';

interface SubmissionMediaItem {
  role: MediaRole;
  url: string;
  type?: string;
}

// Check if submission is multi-photo (has media[] with 2+ items)
function isMultiPhotoSubmission(submission: any): boolean {
  return Array.isArray(submission?.media) && (submission?.media?.length ?? 0) >= 2;
}

// Get required roles for a submission (legacy function, now uses stepKeys internally)
// Phase A: Uses stepKeys internally but returns roles for UI compatibility
function getRequiredRoles(submission: any): MediaRole[] {
  // Use stepKeys internally (derives from submission.media, NOT task.steps)
  const stepKeys = getRequiredSteps(submission);
  
  // Map stepKeys back to roles for UI display (legacy compatibility)
  const roles: MediaRole[] = [];
  for (const stepKey of stepKeys) {
    const role = stepKeyToRole(stepKey, submission);
    if (role && !roles.includes(role)) {
      roles.push(role);
    }
  }
  
  // Fallback: if no roles found, use stepKey as role (for legacy compatibility)
  if (roles.length === 0) {
    return ['primary'];
  }
  
  return roles;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Check if submission is expiration-kind
// ─────────────────────────────────────────────────────────────────────────────
function isExpirationKindSubmission(submission: any): boolean {
  const taskKind = submission?.task?.taskKind;
  const informationType = submission?.task?.informationType || submission?.informationType;
  return taskKind === 'expiration' || informationType === 'Expiration Date';
}

// Get image URL for a specific role
function getImageUrlForRole(submission: any, role: MediaRole): string | null {
  if (!submission) return null;
  
  if (role === 'primary') {
    // Prefer mediaUrl for backward compatibility
    if (submission.mediaUrl) return submission.mediaUrl;
    // Fallback to media array
    if (Array.isArray(submission.media)) {
      const primaryMedia = submission.media.find((m: any) => m.role === 'primary');
      if (primaryMedia?.url) return primaryMedia.url;
    }
    return null;
  }
  
  // For secondary roles, look in media array
  if (Array.isArray(submission.media)) {
    const mediaItem = submission.media.find((m: any) => m.role === role);
    return mediaItem?.url || null;
  }
  
  return null;
}

// Get labels for active role (using stepKeys internally)
function getActiveLabels(submission: any, activeRole: MediaRole): LabelData[] {
  if (!submission) return [];
  
  // Find corresponding stepKey for this role
  const mediaItem = submission.media?.find((m: any) => m.role === activeRole);
  const stepKey = stepKeyFromMedia(mediaItem);
  
  // Use helper to read labels with fallback priority
  return getLabelsForStep(submission, stepKey);
}

// Get display label for a role
function getRoleDisplayLabel(role: MediaRole, submission?: any): string {
  if (role === 'primary') {
    if (submission?.media?.some((m: any) => m.role === 'after')) {
      return 'Before';
    }
    return 'Primary';
  }
  if (role === 'expiration_label') return 'Expiration Label';
  if (role === 'after') return 'After';
  return role;
}

function resolveAttributeDefinitions(
  names: string[] | undefined,
  catalog: LabelAttribute[]
): LabelAttribute[] {
  if (!names?.length) return [];
  return names
    .map((name) => catalog.find((attr) => attr.name === name && attr.active !== false))
    .filter((attr): attr is LabelAttribute => Boolean(attr));
}

function getInstanceAttributeDefs(
  schema: any,
  label: any,
  catalog: LabelAttribute[],
  getCategoryById: (id: string) => any
): LabelAttribute[] {
  const globalNames: string[] = schema?.globalAttributes || [];
  const categoryId = label?.categoryId;
  const schemaClass = schema?.classes?.find(
    (c: any) => c.id === categoryId || c.name === categoryId
  );
  let classNames: string[] = [];
  if (schemaClass?.attributes?.length) {
    classNames = schemaClass.attributes;
  } else {
    const globalCategory = categoryId ? getCategoryById(categoryId) : null;
    if (
      globalCategory &&
      Array.isArray((globalCategory as any).attributes) &&
      (globalCategory as any).attributes.length
    ) {
      classNames = (globalCategory as any).attributes;
    }
  }
  const allNames = Array.from(new Set([...globalNames, ...classNames]));
  return resolveAttributeDefinitions(allNames, catalog);
}

function getImageAttributeDefs(schema: any, catalog: LabelAttribute[]): LabelAttribute[] {
  return resolveAttributeDefinitions(schema?.imageAttributes, catalog);
}

async function persistLabelAtIndex(
  submission: any,
  activeRole: MediaRole,
  labelIndex: number,
  updatedLabel: LabelData
): Promise<any> {
  if (isMultiPhotoSubmission(submission)) {
    const activeMediaItem = submission.media?.find((m: any) => m.role === activeRole);
    const stepKey = stepKeyFromMedia(activeMediaItem);

    const currentRoleLabels = [...(submission.labelsByRole?.[activeRole] || [])];
    currentRoleLabels[labelIndex] = updatedLabel;

    const updatedLabelsByRole = {
      ...submission.labelsByRole,
      [activeRole]: currentRoleLabels,
    };

    const currentStepLabels = [
      ...(submission.labelsByStep?.[stepKey] || submission.labelsByRole?.[activeRole] || []),
    ];
    currentStepLabels[labelIndex] = updatedLabel;

    const updatedLabelsByStep = {
      ...(submission.labelsByStep || {}),
      [stepKey]: currentStepLabels,
    };

    await updateDoc(doc(db, 'submissions', submission.id), {
      labelsByRole: updatedLabelsByRole,
      labelsByStep: updatedLabelsByStep,
    });

    return {
      ...submission,
      labelsByRole: updatedLabelsByRole,
      labelsByStep: updatedLabelsByStep,
    };
  }

  const updatedLabels = [...(submission.labels || [])];
  updatedLabels[labelIndex] = updatedLabel;

  const primaryMediaItem =
    submission.media?.find((m: any) => m.role === 'primary') ||
    (submission.media && submission.media.length > 0 ? submission.media[0] : null);
  const primaryStepKey = stepKeyFromMedia(primaryMediaItem);

  const updatedLabelsByStep = {
    ...(submission.labelsByStep || {}),
    [primaryStepKey]: updatedLabels,
  };

  await updateDoc(doc(db, 'submissions', submission.id), {
    labels: updatedLabels,
    labelsByStep: updatedLabelsByStep,
  });

  return {
    ...submission,
    labels: updatedLabels,
    labelsByStep: updatedLabelsByStep,
  };
}

export default function QA() {
  const [flaggedSubmissions, setFlaggedSubmissions] = useState<any[]>([]);
  const [qaReviewSubmissions, setQaReviewSubmissions] = useState<any[]>([]);
  const [qaApprovalSubmissions, setQaApprovalSubmissions] = useState<any[]>([]);
  const [qaFinalReviewSubmissions, setQaFinalReviewSubmissions] = useState<any[]>([]);
  const [savedForLaterSubmissions, setSavedForLaterSubmissions] = useState<any[]>([]);
  const [savedForLaterFinalReviewSubmissions, setSavedForLaterFinalReviewSubmissions] = useState<any[]>([]);
  const [selectedFlagged, setSelectedFlagged] = useState<any | null>(null);
  const [selectedQaReview, setSelectedQaReview] = useState<any | null>(null);
  const [selectedQaApproval, setSelectedQaApproval] = useState<any | null>(null);
  const [selectedQaFinalReview, setSelectedQaFinalReview] = useState<any | null>(null);
  const [selectedSaved, setSelectedSaved] = useState<any | null>(null);
  const [selectedSavedFinalReview, setSelectedSavedFinalReview] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"qaAutoValidation" | "qaReview" | "qaApprovals" | "qaFinalReview">("qaApprovals");
  const [qaReviewSubTab, setQaReviewSubTab] = useState<"active" | "saved">("active");
  const [qaApprovalSubTab, setQaApprovalSubTab] = useState<"active" | "saved">("active");
  const [qaFinalReviewSubTab, setQaFinalReviewSubTab] = useState<"active" | "saved">("active");
  
  // Flag modal state for QA Final Review
  const [showFlagModalFinalReview, setShowFlagModalFinalReview] = useState(false);
  const [flaggingLabelFinalReview, setFlaggingLabelFinalReview] = useState<{ index: number; label: any } | null>(null);
  const [flagTypeFinalReview, setFlagTypeFinalReview] = useState<'redo' | 'wrong_annotation' | 'annotation_not_needed' | 'other'>('redo');
  const [flagReasonFinalReview, setFlagReasonFinalReview] = useState('');

  type AttributeFlagType = 'wrong_value' | 'cannot_determine' | 'needs_review' | 'other';
  const [showAttributeFlagModalFinalReview, setShowAttributeFlagModalFinalReview] = useState(false);
  const [flaggingAttributeNameFinalReview, setFlaggingAttributeNameFinalReview] = useState<string | null>(null);
  const [attributeFlagTypeFinalReview, setAttributeFlagTypeFinalReview] = useState<AttributeFlagType>('wrong_value');
  const [attributeFlagReasonFinalReview, setAttributeFlagReasonFinalReview] = useState('');
  
  // QA Review modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ category: '', subcategory: '', itemType: '', taskName: '' });
  const [tasks, setTasks] = useState<any[]>([]);
  const [schemas, setSchemas] = useState<Record<string, any>>({});
  const [selectedLabelIndexQaFinalReview, setSelectedLabelIndexQaFinalReview] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'QA — Pixano';
  }, []);
  
  // QA Approvals rejection modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [customRejectReason, setCustomRejectReason] = useState("");
  const [rejectingSubmission, setRejectingSubmission] = useState<any | null>(null);
  
  // Multi-photo role state for each QA tab
  const [activeRoleFlagged, setActiveRoleFlagged] = useState<MediaRole>('primary');
  const [activeRoleQaReview, setActiveRoleQaReview] = useState<MediaRole>('primary');
  const [activeRoleQaApproval, setActiveRoleQaApproval] = useState<MediaRole>('primary');
  const [activeRoleQaFinalReview, setActiveRoleQaFinalReview] = useState<MediaRole>('primary');
  const [activeRoleSaved, setActiveRoleSaved] = useState<MediaRole>('primary');
  const [activeRoleSavedFinalReview, setActiveRoleSavedFinalReview] = useState<MediaRole>('primary');
  
  // StepKey states for step-based tasks
  const [activeStepKeyQaApproval, setActiveStepKeyQaApproval] = useState<string>('primary');
  const [activeStepKeyQaReview, setActiveStepKeyQaReview] = useState<string>('primary');
  const [activeStepKeyQaFinalReview, setActiveStepKeyQaFinalReview] = useState<string>('primary');
  const [activeStepKeySaved, setActiveStepKeySaved] = useState<string>('primary');
  
  // Global categories for proper label names
  const { getCategoryById } = useGlobalCategories();
  const { categories: objectCategories } = useObjectCategories();
  const { attributes: labelAttributes } = useLabelAttributes();
  
  // Create a map for object category names
  const objectCategoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    objectCategories.forEach(cat => {
      map.set(cat.id, cat.displayName || cat.name || cat.id);
    });
    return map;
  }, [objectCategories]);
  
  // Helper function to get category name
  const getCategoryName = (categoryId: string, schema?: any) => {
    // First try to find in schema classes
    const schemaCategory = schema?.classes?.find((c: any) => c.id === categoryId);
    if (schemaCategory) {
      return schemaCategory.label || schemaCategory.name || categoryId;
    }
    
    // Then try to find in object categories
    const objectCategoryName = objectCategoryNameMap.get(categoryId);
    if (objectCategoryName) {
      return objectCategoryName;
    }
    
    // Then try to find in global categories
    const globalCategory = getCategoryById(categoryId);
    if (globalCategory) {
      return globalCategory.displayName || globalCategory.name || categoryId;
    }
    
    // Fallback to categoryId
    return categoryId;
  };

  const getSchemaForSubmission = (submission: any) => {
    const schemaId = submission?.task?.detection?.schemaRef?.id;
    if (!schemaId) return null;
    return schemas[schemaId] || null;
  };

  const qaFinalReviewActiveLabels = useMemo(
    () =>
      selectedQaFinalReview
        ? getActiveLabels(selectedQaFinalReview, activeRoleQaFinalReview)
        : [],
    [selectedQaFinalReview, activeRoleQaFinalReview]
  );

  const qaFinalReviewSchema = selectedQaFinalReview
    ? getSchemaForSubmission(selectedQaFinalReview)
    : null;

  const selectedQaFinalReviewLabel =
    selectedLabelIndexQaFinalReview !== null &&
    selectedLabelIndexQaFinalReview < qaFinalReviewActiveLabels.length
      ? qaFinalReviewActiveLabels[selectedLabelIndexQaFinalReview]
      : null;

  const qaFinalReviewImageAttributes = useMemo(
    () => getImageAttributeDefs(qaFinalReviewSchema, labelAttributes),
    [qaFinalReviewSchema, labelAttributes]
  );

  const selectedQaFinalReviewLabelInstanceAttributes = useMemo(() => {
    if (!selectedQaFinalReviewLabel || !qaFinalReviewSchema) return [];
    return getInstanceAttributeDefs(
      qaFinalReviewSchema,
      selectedQaFinalReviewLabel,
      labelAttributes,
      getCategoryById
    );
  }, [selectedQaFinalReviewLabel, qaFinalReviewSchema, labelAttributes, getCategoryById]);

  useEffect(() => {
    setSelectedLabelIndexQaFinalReview(null);
  }, [selectedQaFinalReview?.id, activeRoleQaFinalReview, activeStepKeyQaFinalReview]);

  const handleQaFinalReviewLabelAttributeChange = async (next: Record<string, any>) => {
    if (!selectedQaFinalReview || selectedLabelIndexQaFinalReview === null) return;

    const labels = getActiveLabels(selectedQaFinalReview, activeRoleQaFinalReview);
    const existingLabel = labels[selectedLabelIndexQaFinalReview];
    if (!existingLabel) return;

    const nextAttributes = { ...next, state_label_source: 'reviewer_verified' };
    const updatedLabel = { ...existingLabel, attributes: nextAttributes };

    try {
      const updatedSubmission = await persistLabelAtIndex(
        selectedQaFinalReview,
        activeRoleQaFinalReview,
        selectedLabelIndexQaFinalReview,
        updatedLabel
      );
      setSelectedQaFinalReview(updatedSubmission);
      setQaFinalReviewSubmissions((prev) =>
        prev.map((s) => (s.id === selectedQaFinalReview.id ? updatedSubmission : s))
      );
    } catch (error) {
      console.error('Error updating label attributes:', error);
      alert('Failed to update label attributes. Please try again.');
    }
  };

  const handleQaFinalReviewImageAttributeChange = async (next: Record<string, any>) => {
    if (!selectedQaFinalReview) return;

    try {
      await updateDoc(doc(db, 'submissions', selectedQaFinalReview.id), {
        imageAttributes: next,
      });
      const updatedSubmission = { ...selectedQaFinalReview, imageAttributes: next };
      setSelectedQaFinalReview(updatedSubmission);
      setQaFinalReviewSubmissions((prev) =>
        prev.map((s) => (s.id === selectedQaFinalReview.id ? updatedSubmission : s))
      );
    } catch (error) {
      console.error('Error updating image attributes:', error);
      alert('Failed to update image attributes. Please try again.');
    }
  };

  const handleQaFinalReviewAttributeFlagClick = (attrName: string) => {
    setFlaggingAttributeNameFinalReview(attrName);
    const existing = selectedQaFinalReviewLabel?.attributeFlags?.[attrName];
    if (existing) {
      setAttributeFlagTypeFinalReview((existing.flagType as AttributeFlagType) || 'wrong_value');
      setAttributeFlagReasonFinalReview(
        existing.flagType === 'other' ? existing.flagReason || '' : ''
      );
    } else {
      setAttributeFlagTypeFinalReview('wrong_value');
      setAttributeFlagReasonFinalReview('');
    }
    setShowAttributeFlagModalFinalReview(true);
  };

  const closeQaFinalReviewAttributeFlagModal = () => {
    setShowAttributeFlagModalFinalReview(false);
    setFlaggingAttributeNameFinalReview(null);
    setAttributeFlagTypeFinalReview('wrong_value');
    setAttributeFlagReasonFinalReview('');
  };

  const handleQaFinalReviewAttributeFlagSubmit = async () => {
    if (
      !selectedQaFinalReview ||
      selectedLabelIndexQaFinalReview === null ||
      !flaggingAttributeNameFinalReview
    )
      return;

    const finalReason =
      attributeFlagTypeFinalReview === 'other'
        ? attributeFlagReasonFinalReview
        : attributeFlagTypeFinalReview;
    if (attributeFlagTypeFinalReview === 'other' && !attributeFlagReasonFinalReview.trim()) {
      alert('Please provide a reason for flagging.');
      return;
    }

    const labels = getActiveLabels(selectedQaFinalReview, activeRoleQaFinalReview);
    const existingLabel = labels[selectedLabelIndexQaFinalReview];
    if (!existingLabel) return;

    const auth = getAuth();
    const uid = auth.currentUser?.uid || 'admin';

    const updatedLabel = {
      ...existingLabel,
      attributeFlags: {
        ...(existingLabel.attributeFlags || {}),
        [flaggingAttributeNameFinalReview]: {
          flagType: attributeFlagTypeFinalReview,
          flagReason: finalReason,
          flaggedAt: Date.now(),
          flaggedBy: uid,
        },
      },
    };

    try {
      const updatedSubmission = await persistLabelAtIndex(
        selectedQaFinalReview,
        activeRoleQaFinalReview,
        selectedLabelIndexQaFinalReview,
        updatedLabel
      );
      setSelectedQaFinalReview(updatedSubmission);
      setQaFinalReviewSubmissions((prev) =>
        prev.map((s) => (s.id === selectedQaFinalReview.id ? updatedSubmission : s))
      );
      closeQaFinalReviewAttributeFlagModal();
    } catch (error) {
      console.error('Error flagging attribute:', error);
      alert('Failed to flag attribute. Please try again.');
    }
  };

  const handleQaFinalReviewAttributeFlagRemove = async () => {
    if (
      !selectedQaFinalReview ||
      selectedLabelIndexQaFinalReview === null ||
      !flaggingAttributeNameFinalReview
    )
      return;

    const labels = getActiveLabels(selectedQaFinalReview, activeRoleQaFinalReview);
    const existingLabel = labels[selectedLabelIndexQaFinalReview];
    if (!existingLabel) return;

    const nextFlags = { ...(existingLabel.attributeFlags || {}) };
    delete nextFlags[flaggingAttributeNameFinalReview];

    const updatedLabel =
      Object.keys(nextFlags).length > 0
        ? { ...existingLabel, attributeFlags: nextFlags }
        : (() => {
            const { attributeFlags: _removed, ...rest } = existingLabel as any;
            return rest;
          })();

    try {
      const updatedSubmission = await persistLabelAtIndex(
        selectedQaFinalReview,
        activeRoleQaFinalReview,
        selectedLabelIndexQaFinalReview,
        updatedLabel
      );
      setSelectedQaFinalReview(updatedSubmission);
      setQaFinalReviewSubmissions((prev) =>
        prev.map((s) => (s.id === selectedQaFinalReview.id ? updatedSubmission : s))
      );
      closeQaFinalReviewAttributeFlagModal();
    } catch (error) {
      console.error('Error removing attribute flag:', error);
      alert('Failed to remove attribute flag. Please try again.');
    }
  };

  const flaggingAttributeDisplayNameFinalReview =
    flaggingAttributeNameFinalReview
      ? selectedQaFinalReviewLabelInstanceAttributes.find(
          (a) => a.name === flaggingAttributeNameFinalReview
        )?.displayName || flaggingAttributeNameFinalReview
      : '';
  
  // Predefined rejection reasons
  const rejectionReasons = [
    "Wrong item photographed",
    "Image quality too poor",
    "Doesn't meet task criteria", 
    "Inappropriate content",
    "Duplicate submission",
    "Other"
  ];

  useEffect(() => {
    const loadSchemas = async () => {
      try {
        const schemasSnapshot = await getDocs(collection(db, 'labelSchemas'));
        const schemasData: Record<string, any> = {};
        schemasSnapshot.docs.forEach((docSnap) => {
          schemasData[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        });
        setSchemas(schemasData);
      } catch (error) {
        console.error('Error loading label schemas:', error);
      }
    };
    loadSchemas();
  }, []);

  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoading(true);
      
      // Fetch all submissions
      const submissionsSnap = await getDocs(collection(db, "submissions"));
      
      // Fetch flagged submissions for QA (Auto Validation)
      const flaggedSubs = submissionsSnap.docs.filter(d => {
        const data = d.data();
        return data.status === "approved" && 
               data.labelingDetails?.labelingFlagged === true;
      });
      
      const flaggedWithTask = await Promise.all(
        flaggedSubs.map(async (sub) => {
          const subData = sub.data();
          const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
          return {
            id: sub.id,
            ...subData,
            task: taskSnap.exists() ? taskSnap.data() : null
          };
        })
      );
      setFlaggedSubmissions(flaggedWithTask);
      setSelectedFlagged(flaggedWithTask[0] || null);

      // Fetch QA Review submissions (sent from workbench)
      const qaReviewSubs = submissionsSnap.docs.filter(d => {
        const data = d.data();
        return data.qaReviewStatus === 'pending';
      });
      
      const qaReviewWithTask = await Promise.all(
        qaReviewSubs.map(async (sub) => {
          const subData = sub.data();
          const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
          return {
            id: sub.id,
            ...subData,
            task: taskSnap.exists() ? taskSnap.data() : null
          };
        })
      );
      setQaReviewSubmissions(qaReviewWithTask);
      setSelectedQaReview(qaReviewWithTask[0] || null);

      // Fetch QA Approval submissions (sent from Task Approvals)
      const qaApprovalSubs = submissionsSnap.docs.filter(d => {
        const data = d.data();
        return data.qaApprovalStatus === 'pending';
      });
      
      const qaApprovalWithTask = await Promise.all(
        qaApprovalSubs.map(async (sub) => {
          const subData = sub.data();
          const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
          return {
            id: sub.id,
            ...subData,
            task: taskSnap.exists() ? taskSnap.data() : null
          };
        })
      );
      setQaApprovalSubmissions(qaApprovalWithTask);
      setSelectedQaApproval(qaApprovalWithTask[0] || null);

      // Fetch QA Final Review submissions (sent from Review page)
      const qaFinalReviewSubs = submissionsSnap.docs.filter(d => {
        const data = d.data();
        return data.qaFinalReviewStatus === 'pending';
      });
      
      const qaFinalReviewWithTask = await Promise.all(
        qaFinalReviewSubs.map(async (sub) => {
          const subData = sub.data();
          const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
          return {
            id: sub.id,
            ...subData,
            task: taskSnap.exists() ? taskSnap.data() : null
          };
        })
      );
      setQaFinalReviewSubmissions(qaFinalReviewWithTask);
      setSelectedQaFinalReview(qaFinalReviewWithTask[0] || null);

      // Fetch saved for later submissions (QA Review)
      const savedSubs = submissionsSnap.docs.filter(d => {
        const data = d.data();
        return data.qaReviewStatus === 'saved_for_later';
      });
      
      const savedWithTask = await Promise.all(
        savedSubs.map(async (sub) => {
          const subData = sub.data();
          const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
          return {
            id: sub.id,
            ...subData,
            task: taskSnap.exists() ? taskSnap.data() : null
          };
        })
      );
      setSavedForLaterSubmissions(savedWithTask);
      setSelectedSaved(savedWithTask[0] || null);

      // Fetch saved for later submissions (QA Final Review)
      const savedFinalReviewSubs = submissionsSnap.docs.filter(d => {
        const data = d.data();
        return data.qaFinalReviewStatus === 'saved_for_later';
      });
      
      const savedFinalReviewWithTask = await Promise.all(
        savedFinalReviewSubs.map(async (sub) => {
          const subData = sub.data();
          const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
          return {
            id: sub.id,
            ...subData,
            task: taskSnap.exists() ? taskSnap.data() : null
          };
        })
      );
      setSavedForLaterFinalReviewSubmissions(savedFinalReviewWithTask);
      setSelectedSavedFinalReview(savedFinalReviewWithTask[0] || null);

      // Fetch tasks for edit modal
      const tasksSnap = await getDocs(collection(db, "tasks"));
      setTasks(tasksSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      setLoading(false);
    };

    fetchSubmissions();
  }, []);

  // Reset activeRole when selections change
  useEffect(() => { setActiveRoleFlagged('primary'); }, [selectedFlagged?.id]);
  
  useEffect(() => {
    if (selectedQaReview) {
      setActiveRoleQaReview('primary');
      const firstStepKey = getRequiredSteps(selectedQaReview)[0] || 'primary';
      setActiveStepKeyQaReview(firstStepKey);
    }
  }, [selectedQaReview?.id]);
  
  useEffect(() => { setActiveRoleQaApproval('primary'); }, [selectedQaApproval?.id]);
  useEffect(() => { setActiveRoleQaFinalReview('primary'); }, [selectedQaFinalReview?.id]);
  
  useEffect(() => {
    if (selectedSaved) {
      setActiveRoleSaved('primary');
      const firstStepKey = getRequiredSteps(selectedSaved)[0] || 'primary';
      setActiveStepKeySaved(firstStepKey);
    }
  }, [selectedSaved?.id]);
  
  useEffect(() => { setActiveRoleSavedFinalReview('primary'); }, [selectedSavedFinalReview?.id]);

  const handleQAOverride = async (action: "correct" | "retrain") => {
    if (!selectedFlagged) return;
    
    try {
      const updateData: any = {
        'labelingDetails.labelingFlagged': false,
        'labelingDetails.qaReviewedAt': Date.now(),
        'labelingDetails.qaReviewedBy': 'admin'
      };

      if (action === "retrain") {
        updateData['labelingDetails.retrainRequested'] = true;
        updateData['labelingDetails.retrainReason'] = "Incorrect label match";
      }

      await updateDoc(doc(db, "submissions", selectedFlagged.id), updateData);

      // Remove from flagged list
      setFlaggedSubmissions(flaggedSubmissions.filter(p => p.id !== selectedFlagged.id));
      setSelectedFlagged(null);

      // Show success message
      const actionText = action === "correct" ? "marked as correct" : "sent for retraining";
      console.log(`✅ Submission ${actionText} successfully`);
      alert(`Submission ${actionText} successfully!`);
    } catch (error) {
      console.error("Error updating flagged submission:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  // QA Review Actions
  const handleApproveToWorkbench = async () => {
    if (!selectedQaReview) return;
    
    try {
      // Clear QA review status and ensure it's approved so it appears in workbench queue
      await updateDoc(doc(db, "submissions", selectedQaReview.id), {
        qaReviewStatus: null,
        status: 'approved',
        approvedAt: Date.now(),
        qaReviewedAt: Date.now(),
        qaReviewedBy: 'admin',
        qaAction: 'approved_to_workbench'
      });

      // Remove from QA Review list
      setQaReviewSubmissions(qaReviewSubmissions.filter(p => p.id !== selectedQaReview.id));
      setSelectedQaReview(qaReviewSubmissions.find(p => p.id !== selectedQaReview.id) || qaReviewSubmissions[0] || null);
      
      alert('Submission approved and sent back to workbench queue!');
    } catch (error) {
      console.error("Error approving to workbench:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const handleApprovePhotoOnly = async () => {
    const submission = selectedQaApproval || (qaReviewSubTab === "saved" ? selectedSaved : (activeTab === "qaFinalReview" ? selectedQaFinalReview : selectedQaReview));
    if (!submission) return;
    
    try {
      const updateData: any = {
        status: 'approved',
        approvedAt: Date.now(),
        qaAction: 'approved_photo_only'
      };

      if (selectedQaApproval) {
        updateData.qaApprovalStatus = null;
        updateData.qaApprovalReviewedAt = Date.now();
        updateData.qaApprovalReviewedBy = 'admin';
      } else if (activeTab === "qaFinalReview" && selectedQaFinalReview) {
        updateData.qaFinalReviewStatus = null;
        updateData.qaFinalReviewReviewedAt = Date.now();
        updateData.qaFinalReviewReviewedBy = 'admin';
      } else {
        updateData.qaReviewStatus = null;
        updateData.qaReviewedAt = Date.now();
        updateData.qaReviewedBy = 'admin';
      }

      await updateDoc(doc(db, "submissions", submission.id), updateData);

      if (selectedQaApproval) {
        setQaApprovalSubmissions(qaApprovalSubmissions.filter(p => p.id !== submission.id));
        setSelectedQaApproval(qaApprovalSubmissions.find(p => p.id !== submission.id) || qaApprovalSubmissions[0] || null);
      } else if (qaReviewSubTab === "saved") {
        setSavedForLaterSubmissions(savedForLaterSubmissions.filter(p => p.id !== submission.id));
        setSelectedSaved(savedForLaterSubmissions.find(p => p.id !== submission.id) || savedForLaterSubmissions[0] || null);
      } else if (activeTab === "qaFinalReview" && selectedQaFinalReview) {
        setQaFinalReviewSubmissions(qaFinalReviewSubmissions.filter(p => p.id !== submission.id));
        setSelectedQaFinalReview(qaFinalReviewSubmissions.find(p => p.id !== submission.id) || qaFinalReviewSubmissions[0] || null);
      } else {
        setQaReviewSubmissions(qaReviewSubmissions.filter(p => p.id !== submission.id));
        setSelectedQaReview(qaReviewSubmissions.find(p => p.id !== submission.id) || qaReviewSubmissions[0] || null);
      }
      
      alert('Photo approved and saved!');
    } catch (error) {
      console.error("Error approving photo only:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const handleEditSubmission = (submission?: any) => {
    const subToEdit = submission || selectedQaApproval || selectedQaReview || selectedQaFinalReview;
    if (!subToEdit) return;
    setEditingSubmission(subToEdit);
    setEditForm({
      category: subToEdit.category || subToEdit.task?.category || '',
      subcategory: subToEdit.subcategory || subToEdit.task?.subcategory || '',
      itemType: subToEdit.itemType || subToEdit.task?.itemType || '',
      taskName: subToEdit.task?.title || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingSubmission) return;
    
    try {
      const updateData: any = {
        category: editForm.category,
        subcategory: editForm.subcategory,
        itemType: editForm.itemType,
        qaEditedAt: Date.now(),
        qaEditedBy: 'admin'
      };

      await updateDoc(doc(db, "submissions", editingSubmission.id), updateData);

      // Update local state for all lists
      const isQAApproval = editingSubmission.qaApprovalStatus === 'pending' || editingSubmission.qaApprovalStatus === 'saved_for_later';
      const isQAFinalReview = editingSubmission.qaFinalReviewStatus === 'pending' || editingSubmission.qaFinalReviewStatus === 'saved_for_later';
      const isSavedForLater = editingSubmission.qaReviewStatus === 'saved_for_later';
      
      if (isQAApproval) {
        const updated = qaApprovalSubmissions.map(s => 
          s.id === editingSubmission.id 
            ? { ...s, ...updateData }
            : s
        );
        setQaApprovalSubmissions(updated);
        setSelectedQaApproval(updated.find(s => s.id === editingSubmission.id) || null);
      } else if (isQAFinalReview) {
        const updated = qaFinalReviewSubmissions.map(s => 
          s.id === editingSubmission.id 
            ? { ...s, ...updateData }
            : s
        );
        setQaFinalReviewSubmissions(updated);
        setSelectedQaFinalReview(updated.find(s => s.id === editingSubmission.id) || null);
      } else if (isSavedForLater) {
        const updated = savedForLaterSubmissions.map(s => 
          s.id === editingSubmission.id 
            ? { ...s, ...updateData }
            : s
        );
        setSavedForLaterSubmissions(updated);
        setSelectedSaved(updated.find(s => s.id === editingSubmission.id) || null);
      } else {
        const updated = qaReviewSubmissions.map(s => 
          s.id === editingSubmission.id 
            ? { ...s, ...updateData }
            : s
        );
        setQaReviewSubmissions(updated);
        setSelectedQaReview(updated.find(s => s.id === editingSubmission.id) || null);
      }
      
      setShowEditModal(false);
      setEditingSubmission(null);
      alert('Submission updated successfully!');
    } catch (error) {
      console.error("Error updating submission:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const handleDeleteSubmission = async () => {
    const submission = selectedQaApproval || (qaReviewSubTab === "saved" ? selectedSaved : (activeTab === "qaFinalReview" ? selectedQaFinalReview : selectedQaReview));
    if (!submission) return;
    
    try {
      await deleteDoc(doc(db, "submissions", submission.id));
      
      if (selectedQaApproval) {
        setQaApprovalSubmissions(qaApprovalSubmissions.filter(p => p.id !== submission.id));
        setSelectedQaApproval(qaApprovalSubmissions.find(p => p.id !== submission.id) || qaApprovalSubmissions[0] || null);
      } else if (activeTab === "qaFinalReview" && selectedQaFinalReview) {
        setQaFinalReviewSubmissions(qaFinalReviewSubmissions.filter(p => p.id !== submission.id));
        setSelectedQaFinalReview(qaFinalReviewSubmissions.find(p => p.id !== submission.id) || qaFinalReviewSubmissions[0] || null);
      } else if (qaReviewSubTab === "saved") {
        setSavedForLaterSubmissions(savedForLaterSubmissions.filter(p => p.id !== submission.id));
        setSelectedSaved(savedForLaterSubmissions.find(p => p.id !== submission.id) || savedForLaterSubmissions[0] || null);
      } else {
        setQaReviewSubmissions(qaReviewSubmissions.filter(p => p.id !== submission.id));
        setSelectedQaReview(qaReviewSubmissions.find(p => p.id !== submission.id) || qaReviewSubmissions[0] || null);
      }
      setShowDeleteConfirm(false);
      
      alert('Submission permanently deleted!');
    } catch (error) {
      console.error("Error deleting submission:", error);
      alert("Error deleting submission. Please try again.");
    }
  };

  const handleSaveForLater = async () => {
    const submission = activeTab === "qaFinalReview" ? (qaFinalReviewSubTab === "saved" ? selectedSavedFinalReview : selectedQaFinalReview) : selectedQaReview;
    if (!submission) return;
    
    try {
      if (activeTab === "qaFinalReview" && selectedQaFinalReview) {
        await updateDoc(doc(db, "submissions", selectedQaFinalReview.id), {
          qaFinalReviewStatus: 'saved_for_later',
          qaFinalReviewSavedAt: Date.now(),
          qaFinalReviewSavedBy: 'admin'
        });
        // Move to saved for later list
        setQaFinalReviewSubmissions(qaFinalReviewSubmissions.filter(p => p.id !== selectedQaFinalReview.id));
        setSavedForLaterFinalReviewSubmissions([...savedForLaterFinalReviewSubmissions, { ...selectedQaFinalReview, qaFinalReviewStatus: 'saved_for_later' }]);
        setSelectedQaFinalReview(qaFinalReviewSubmissions.find(p => p.id !== selectedQaFinalReview.id) || qaFinalReviewSubmissions[0] || null);
      } else {
        await updateDoc(doc(db, "submissions", selectedQaReview.id), {
          qaReviewStatus: 'saved_for_later',
          qaSavedAt: Date.now(),
          qaSavedBy: 'admin'
        });
        // Move to saved for later list
        setQaReviewSubmissions(qaReviewSubmissions.filter(p => p.id !== selectedQaReview.id));
        setSavedForLaterSubmissions([...savedForLaterSubmissions, { ...selectedQaReview, qaReviewStatus: 'saved_for_later' }]);
        setSelectedQaReview(qaReviewSubmissions.find(p => p.id !== selectedQaReview.id) || qaReviewSubmissions[0] || null);
      }
      
      alert('Submission saved for later!');
    } catch (error) {
      console.error("Error saving for later:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const handleFinalApproval = async () => {
    if (!selectedQaFinalReview) return;
    
    try {
      const auth = getAuth();
      const currentUserId = auth.currentUser?.uid || 'admin';
      
      // Build update data
      const updateData: any = {
        qaFinalReviewStatus: null,
        status: 'completed',
        completedAt: serverTimestamp(),
        qaFinalReviewReviewedAt: Date.now(),
        qaFinalReviewReviewedBy: currentUserId,
        qaAction: 'final_approval'
      };
      
      // For expiration-kind submissions, set itemTypeStage to 'final' and packaging eligibility
      if (isExpirationKindSubmission(selectedQaFinalReview)) {
        updateData.itemTypeStage = 'final';
        updateData.packagingEligibility = {
          photoPackagesEligible: true,
          datasetPackagesEligible: true,
          eligibleAt: Date.now(),
          eligibleBy: currentUserId
        };
      }
      
      // Set status to 'completed' to send to data packages (DatasetPackages queries for status === 'completed')
      await updateDoc(doc(db, "submissions", selectedQaFinalReview.id), updateData);

      // Remove from QA Final Review list
      setQaFinalReviewSubmissions(qaFinalReviewSubmissions.filter(p => p.id !== selectedQaFinalReview.id));
      setSelectedQaFinalReview(qaFinalReviewSubmissions.find(p => p.id !== selectedQaFinalReview.id) || qaFinalReviewSubmissions[0] || null);
      
      alert('Submission given final approval and sent to data packages!');
    } catch (error) {
      console.error("Error giving final approval:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  // Flag handlers for QA Final Review
  const handleFlagClickFinalReview = (labelIndex: number, label: any) => {
    setFlaggingLabelFinalReview({ index: labelIndex, label });
    setFlagTypeFinalReview('redo');
    setFlagReasonFinalReview('');
    setShowFlagModalFinalReview(true);
  };

  const handleFlagSubmitFinalReview = async () => {
    if (!selectedQaFinalReview || !flaggingLabelFinalReview) return;
    
    const finalReason = flagTypeFinalReview === 'other' ? flagReasonFinalReview : flagTypeFinalReview;
    if (flagTypeFinalReview === 'other' && !flagReasonFinalReview.trim()) {
      alert('Please provide a reason for flagging.');
      return;
    }

    try {
      if (isMultiPhotoSubmission(selectedQaFinalReview)) {
        // Multi-photo: dual-write to labelsByRole (legacy) and labelsByStep (new)
        // CRITICAL: Use activeStepKey (for step-based) or derive from activeRole (legacy)
        const currentStepKey = activeStepKeyQaFinalReview || stepKeyFromMedia(selectedQaFinalReview.media?.find((m: any) => m.role === activeRoleQaFinalReview));
        
        const currentRoleLabels = [...(selectedQaFinalReview.labelsByRole?.[activeRoleQaFinalReview] || [])];
        currentRoleLabels[flaggingLabelFinalReview.index] = {
          ...currentRoleLabels[flaggingLabelFinalReview.index],
          flagged: true,
          flagType: flagTypeFinalReview,
          flagReason: finalReason
        };

        const updatedLabelsByRole = {
          ...selectedQaFinalReview.labelsByRole,
          [activeRoleQaFinalReview]: currentRoleLabels
        };
        
        // Also update labelsByStep with same change
        const currentStepLabels = [...(selectedQaFinalReview.labelsByStep?.[currentStepKey] || selectedQaFinalReview.labelsByRole?.[activeRoleQaFinalReview] || [])];
        currentStepLabels[flaggingLabelFinalReview.index] = {
          ...currentStepLabels[flaggingLabelFinalReview.index],
          flagged: true,
          flagType: flagTypeFinalReview,
          flagReason: finalReason
        };
        
        const updatedLabelsByStep = {
          ...(selectedQaFinalReview.labelsByStep || {}),
          [currentStepKey]: currentStepLabels
        };

        await updateDoc(doc(db, 'submissions', selectedQaFinalReview.id), {
          labelsByRole: updatedLabelsByRole,
          labelsByStep: updatedLabelsByStep
        });

        const updatedSubmission = {
          ...selectedQaFinalReview,
          labelsByRole: updatedLabelsByRole,
          labelsByStep: updatedLabelsByStep
        };
        setSelectedQaFinalReview(updatedSubmission);
        setQaFinalReviewSubmissions(qaFinalReviewSubmissions.map(s => s.id === selectedQaFinalReview.id ? updatedSubmission : s));
      } else {
        // Single-photo: update legacy labels field, also add labelsByStep['primary']
        const updatedLabels = [...(selectedQaFinalReview.labels || [])];
        updatedLabels[flaggingLabelFinalReview.index] = {
          ...updatedLabels[flaggingLabelFinalReview.index],
          flagged: true,
          flagType: flagTypeFinalReview,
          flagReason: finalReason
        };
        
        // Derive stepKey for single-photo
        const primaryMediaItem = selectedQaFinalReview.media?.find((m: any) => m.role === 'primary') || 
                                 (selectedQaFinalReview.media && selectedQaFinalReview.media.length > 0 ? selectedQaFinalReview.media[0] : null);
        const primaryStepKey = stepKeyFromMedia(primaryMediaItem);
        
        const updatedLabelsByStep = {
          ...(selectedQaFinalReview.labelsByStep || {}),
          [primaryStepKey]: updatedLabels
        };

        await updateDoc(doc(db, 'submissions', selectedQaFinalReview.id), {
          labels: updatedLabels,  // Legacy format
          labelsByStep: updatedLabelsByStep  // New format (additive)
        });

        const updatedSubmission = {
          ...selectedQaFinalReview,
          labels: updatedLabels,
          labelsByStep: updatedLabelsByStep
        };
        setSelectedQaFinalReview(updatedSubmission);
        setQaFinalReviewSubmissions(qaFinalReviewSubmissions.map(s => s.id === selectedQaFinalReview.id ? updatedSubmission : s));
      }

      // Close modal
      setShowFlagModalFinalReview(false);
      setFlaggingLabelFinalReview(null);
      setFlagTypeFinalReview('redo');
      setFlagReasonFinalReview('');
    } catch (error) {
      console.error('Error flagging label:', error);
      alert('Failed to flag label. Please try again.');
    }
  };

  const handleApproveFinalReviewToWorkbench = async () => {
    if (!selectedQaFinalReview) return;
    
    try {
      // Clear QA Final Review status and ensure it's approved so it appears in workbench queue
      await updateDoc(doc(db, "submissions", selectedQaFinalReview.id), {
        qaFinalReviewStatus: null,
        status: 'approved',
        approvedAt: Date.now(),
        qaFinalReviewReviewedAt: Date.now(),
        qaFinalReviewReviewedBy: 'admin',
        qaAction: 'approved_to_workbench'
      });

      // Remove from QA Final Review list
      setQaFinalReviewSubmissions(qaFinalReviewSubmissions.filter(p => p.id !== selectedQaFinalReview.id));
      setSelectedQaFinalReview(qaFinalReviewSubmissions.find(p => p.id !== selectedQaFinalReview.id) || qaFinalReviewSubmissions[0] || null);
      
      alert('Submission approved and sent back to workbench queue!');
    } catch (error) {
      console.error("Error approving to workbench:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const handleSendSavedToWorkbench = async (submission?: any) => {
    const subToSend = submission || selectedSaved;
    if (!subToSend) return;
    
    try {
      await updateDoc(doc(db, "submissions", subToSend.id), {
        qaReviewStatus: null,
        status: 'approved',
        approvedAt: Date.now(),
        qaReviewedAt: Date.now(),
        qaReviewedBy: 'admin',
        qaAction: 'approved_to_workbench'
      });

      setSavedForLaterSubmissions(savedForLaterSubmissions.filter(p => p.id !== subToSend.id));
      setSelectedSaved(savedForLaterSubmissions.find(p => p.id !== subToSend.id) || savedForLaterSubmissions[0] || null);
      alert('Submission sent to workbench!');
    } catch (error) {
      console.error("Error sending to workbench:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  // QA Approvals Actions - matches Task Approvals exactly
  const handleQAApprovalAction = async (status: "approved" | "rejected", rejectReason?: string) => {
    if (!selectedQaApproval) return;
    
    try {
      // Prepare update data - exactly like Task Approvals
      const updateData: any = {
        qaApprovalStatus: null, // Clear QA approval status
        status,
        adminReviewedAt: Date.now(),
        adminReviewedBy: "admin"
      };

      // Add approvedAt timestamp for approved submissions (needed for workbench queue and other systems)
      // For Photo tasks, this will also make it appear in Photo Packages (PhotoPackages queries for taskType === 'Photo' AND status === 'approved')
      if (status === "approved") {
        updateData.approvedAt = Date.now();
        console.log(`✅ [QA Approvals] Approving submission ${selectedQaApproval.id} - will appear in workbench queue if task has detection enabled`);
        
        // Log if this is a Photo task (will appear in Photo Packages)
        if (selectedQaApproval.taskType === 'Photo' || selectedQaApproval.task?.taskType === 'Photo') {
          console.log(`📸 [QA Approvals] Photo task approved - will appear in Photo Packages`);
        }
      }

      // Add rejection reason if provided
      if (status === "rejected" && rejectReason) {
        updateData.rejectReason = rejectReason;
        updateData.rejectedAt = Date.now();
        updateData.rejectedBy = "admin";
      }

      // Update the submission document
      console.log(`📝 [QA Approvals] Updating submission ${selectedQaApproval.id} with status: ${status}`);
      await updateDoc(doc(db, "submissions", selectedQaApproval.id), updateData);
      console.log(`✅ [QA Approvals] Submission ${selectedQaApproval.id} updated successfully in submissions collection`);
      
      // Log task info for debugging workbench queue
      if (status === "approved" && selectedQaApproval.task) {
        const hasDetection = selectedQaApproval.task.detection?.enabled && selectedQaApproval.task.detection?.schemaRef?.id;
        console.log(`🔍 [QA Approvals] Task ${selectedQaApproval.taskId} detection info:`, {
          enabled: selectedQaApproval.task.detection?.enabled,
          hasSchema: !!selectedQaApproval.task.detection?.schemaRef?.id,
          schemaId: selectedQaApproval.task.detection?.schemaRef?.id,
          willAppearInWorkbench: hasDetection
        });
      }

      // Also update the user's completedTasks - exactly like Task Approvals
      const userRef = doc(db, "users", selectedQaApproval.userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const completedTasks = userData.completedTasks || {};
        const taskData = completedTasks[selectedQaApproval.taskId];
        
        if (taskData && Array.isArray(taskData.submissions)) {
          // Update the specific submission in the user's completedTasks
          // Match by submissionId, id, or mediaUrl to handle different data formats
          const updatedSubmissions = taskData.submissions.map((sub: any) => {
            const matches = 
              sub.submissionId === selectedQaApproval.id || 
              sub.id === selectedQaApproval.id ||
              (sub.mediaUrl && selectedQaApproval.mediaUrl && sub.mediaUrl === selectedQaApproval.mediaUrl);
            
            if (matches) {
              const submissionUpdate: any = {
                ...sub,
                status,
                adminReviewedAt: Date.now(),
                adminReviewedBy: "admin"
              };
              
              // Ensure submissionId is set if it wasn't before
              if (!submissionUpdate.submissionId) {
                submissionUpdate.submissionId = selectedQaApproval.id;
              }
              
              // Add rejection reason if provided
              if (status === "rejected" && rejectReason) {
                submissionUpdate.rejectReason = rejectReason;
                submissionUpdate.rejectedAt = Date.now();
                submissionUpdate.rejectedBy = "admin";
              }
              
              return submissionUpdate;
            }
            return sub;
          });
          
          // Get reward from task (needed for both approved and rejected)
          const reward = Number(selectedQaApproval.task?.reward) || Number(selectedQaApproval.reward) || 0;
          
          // Update the task-level status
          const updatedTaskData = {
            ...taskData,
            status,
            submissions: updatedSubmissions,
            adminReviewedAt: Date.now(),
            adminReviewedBy: "admin"
          };
          
          // Add rejection reason to task level if provided
          if (status === "rejected" && rejectReason) {
            updatedTaskData.rejectReason = rejectReason;
            updatedTaskData.rejectedAt = Date.now();
            updatedTaskData.rejectedBy = "admin";
          }
          
          // Apply cooldown and PIX reward ONLY for approved submissions
          if (status === "approved") {
            // Apply cooldown by updating lastSubmittedAt
            updatedTaskData.lastSubmittedAt = Date.now();
            
            // Ensure reward is saved in the matching submission object
            const finalUpdatedSubmissions = updatedSubmissions.map((sub: any) => {
              const matches = 
                sub.submissionId === selectedQaApproval.id || 
                sub.id === selectedQaApproval.id ||
                (sub.mediaUrl && selectedQaApproval.mediaUrl && sub.mediaUrl === selectedQaApproval.mediaUrl);
              
              if (matches && sub.status === 'approved') {
                return {
                  ...sub,
                  reward: reward, // Always set reward for approved submissions
                  rewardAmount: reward // Also add rewardAmount for compatibility
                };
              }
              return sub;
            });
            
            // Update the submissions array with rewards
            updatedTaskData.submissions = finalUpdatedSubmissions;
            
            // Also set reward at task level if not already set
            if (reward > 0 && !updatedTaskData.reward) {
              updatedTaskData.reward = reward;
            }
            
            if (reward > 0) {
              // Combine both updates into a single call to avoid partial failures
              await updateDoc(userRef, {
                pixBalance: arrayUnion(reward),
                [`completedTasks.${selectedQaApproval.taskId}`]: updatedTaskData
              });
              console.log(`✅ Applied cooldown and ${reward} PIX reward for approved submission (from task: ${selectedQaApproval.task?.reward})`);
            } else {
              // No reward, just update completedTasks
              await updateDoc(userRef, {
                [`completedTasks.${selectedQaApproval.taskId}`]: updatedTaskData
              });
              console.log(`✅ Applied cooldown for approved submission (no reward - task reward: ${selectedQaApproval.task?.reward})`);
            }
            
            // Warn if task doesn't have detection enabled (won't appear in workbench queue)
            if (!selectedQaApproval.task?.detection?.enabled || !selectedQaApproval.task?.detection?.schemaRef?.id) {
              console.warn(`⚠️ [QA Approvals] WARNING: Task ${selectedQaApproval.taskId} does not have detection enabled or schema assigned. Submission will NOT appear in workbench queue.`);
              console.warn(`⚠️ [QA Approvals] Task detection status:`, {
                enabled: selectedQaApproval.task?.detection?.enabled,
                hasSchema: !!selectedQaApproval.task?.detection?.schemaRef?.id,
                schemaId: selectedQaApproval.task?.detection?.schemaRef?.id
              });
            } else {
              console.log(`✅ [QA Approvals] Task ${selectedQaApproval.taskId} has detection enabled - submission should appear in workbench queue`);
            }
          } else {
            // For rejected submissions, don't apply cooldown - allow immediate retry
            await updateDoc(userRef, {
              [`completedTasks.${selectedQaApproval.taskId}`]: updatedTaskData
            });
            console.log(`❌ Submission rejected - no cooldown applied, user can retry immediately`);
          }
        }
      }

      // Remove from the list and select the next submission
      const updatedSubmissions = qaApprovalSubmissions.filter(p => p.id !== selectedQaApproval.id);
      setQaApprovalSubmissions(updatedSubmissions);
      // Select the next submission in the queue (or first one if we removed the first)
      setSelectedQaApproval(updatedSubmissions.length > 0 ? updatedSubmissions[0] : null);
      
      // Close rejection modal if it was open
      if (status === "rejected") {
        setShowRejectModal(false);
        setRejectReason("");
        setCustomRejectReason("");
        setRejectingSubmission(null);
      }
    } catch (error: any) {
      console.error("Error updating submission:", error);
      console.error("Error details:", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });
      
      // Even if user document update fails, the submission update succeeded
      // So we should still update the UI to reflect the submission change
      const updatedSubmissions = qaApprovalSubmissions.filter(p => p.id !== selectedQaApproval.id);
      setQaApprovalSubmissions(updatedSubmissions);
      setSelectedQaApproval(updatedSubmissions.length > 0 ? updatedSubmissions[0] : null);
      
      // Show a warning but don't block the UI
      alert(`Submission ${status} successfully, but there was an issue updating user data: ${error?.message || 'Unknown error'}. The submission has been processed.`);
    }
  };

  const handleQAApprovalRejectClick = () => {
    setRejectingSubmission(selectedQaApproval);
    setShowRejectModal(true);
    setRejectReason("");
    setCustomRejectReason("");
  };

  const handleQAApprovalRejectReasonChange = (reason: string) => {
    setRejectReason(reason);
    if (reason !== "Other") {
      setCustomRejectReason("");
    }
  };

  const handleQAApprovalRejectSubmit = () => {
    const finalReason = rejectReason === "Other" ? customRejectReason : rejectReason;
    if (!finalReason.trim()) {
      alert("Please provide a rejection reason.");
      return;
    }
    handleQAApprovalAction("rejected", finalReason);
  };

  const handleQAApprovalRejectModalClose = () => {
    setShowRejectModal(false);
    setRejectReason("");
    setCustomRejectReason("");
    setRejectingSubmission(null);
  };


  const handleQAApprovalSaveForLater = async () => {
    if (!selectedQaApproval) return;
    
    try {
      await updateDoc(doc(db, "submissions", selectedQaApproval.id), {
        qaApprovalStatus: 'saved_for_later',
        qaApprovalSavedAt: Date.now(),
        qaApprovalSavedBy: 'admin'
      });

      // Move to saved for later list (we'll need to track this separately)
      setQaApprovalSubmissions(qaApprovalSubmissions.filter(p => p.id !== selectedQaApproval.id));
      setSelectedQaApproval(qaApprovalSubmissions.find(p => p.id !== selectedQaApproval.id) || qaApprovalSubmissions[0] || null);
      
      alert('Submission saved for later!');
    } catch (error) {
      console.error("Error saving for later:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const getConfidenceBadge = (confidence: string) => {
    if (confidence === "low") {
      return <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">⚠️ Low Confidence</span>;
    }
    return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">✓ High Confidence</span>;
  };

  const getSimilarityInfo = (submission: any) => {
    if (submission.validationDetails?.similarity) {
      return (
        <div className="mb-2">
          <span className="font-semibold">Similarity Score: </span>
          <span className={`${submission.validationDetails.similarity >= 0.6 ? 'text-green-600' : 'text-yellow-600'}`}>
            {(submission.validationDetails.similarity * 100).toFixed(1)}%
          </span>
        </div>
      );
    }
    return null;
  };

  const getLabelingInfo = (submission: any) => {
    if (!submission.labelingDetails) return null;

    const details = submission.labelingDetails;
    return (
      <div className="mb-4 p-3 bg-gray-50 rounded border">
        <h4 className="font-semibold mb-2">Labeling Details</h4>
        <div className="mb-2">
          <span className="font-semibold">Match Confidence: </span>
          {getConfidenceBadge(details.matchConfidence)}
        </div>
        <div className="mb-2">
          <span className="font-semibold">Labels: </span>
          <span className="text-sm text-gray-600">
            {submission.labels?.join(', ') || 'None'}
          </span>
        </div>
        <div className="mb-2">
          <span className="font-semibold">Expected Keywords: </span>
          <span className="text-sm text-gray-600">
            {details.verificationDetails?.expectedKeywords?.join(', ') || 'None'}
          </span>
        </div>
        <div className="mb-2">
          <span className="font-semibold">Matched Keywords: </span>
          <span className="text-sm text-gray-600">
            {details.verificationDetails?.matchedKeywords?.join(', ') || 'None'}
          </span>
        </div>
        <div className="text-xs text-gray-500">
          Match: {details.verificationDetails?.matchCount || 0} / {details.verificationDetails?.totalExpected || 0}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header: Title and tabs on same line */}
      <div className="flex items-center justify-between border-b mb-4 pb-2">
        <h1 className="text-3xl font-bold">QA</h1>
        <div className="flex items-center gap-4">
          <div className="flex">
            <button
              className={`px-4 py-2 font-medium ${activeTab === "qaApprovals" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("qaApprovals")}
            >
              QA Approvals ({qaApprovalSubmissions.length})
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === "qaAutoValidation" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("qaAutoValidation")}
            >
              QA (Auto Validation) ({flaggedSubmissions.length})
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === "qaReview" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("qaReview")}
            >
              QA Workbench ({qaReviewSubmissions.length})
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === "qaFinalReview" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("qaFinalReview")}
            >
              QA Final Review ({qaFinalReviewSubmissions.length})
            </button>
          </div>
        </div>
      </div>

      {activeTab === "qaApprovals" && (
        <div>
          {/* Sub-tabs for QA Approvals */}
          <div className="border-b mb-4">
            <div className="flex">
              <button
                className={`px-4 py-2 font-medium ${qaApprovalSubTab === "active" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setQaApprovalSubTab("active")}
              >
                Active ({qaApprovalSubmissions.length})
              </button>
              <button
                className={`px-4 py-2 font-medium ${qaApprovalSubTab === "saved" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setQaApprovalSubTab("saved")}
              >
                Save for Later (0)
              </button>
            </div>
          </div>

          {qaApprovalSubTab === "active" && (
            <div className="flex gap-4">
              <div className="flex-1 border p-4 min-h-[300px]">
                {loading ? (
                  <div>Loading...</div>
                ) : selectedQaApproval ? (
                  <div>
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <button
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                        onClick={() => handleQAApprovalAction("approved")}
                      >
                        Approve
                      </button>
                      <button
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                        onClick={handleQAApprovalRejectClick}
                      >
                        Reject
                      </button>
                      <button
                        className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-600"
                        onClick={handleApprovePhotoOnly}
                      >
                        Approve Photo Only
                      </button>
                      <button
                        className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                        onClick={() => handleEditSubmission(selectedQaApproval)}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
                        onClick={handleQAApprovalSaveForLater}
                      >
                        Save for Later
                      </button>
                    </div>
                    
                    <h2 className="text-xl font-semibold mb-4">QA Approvals Submission</h2>
                    
                    {/* Multi-photo Step/Role Toggle */}
                    {isMultiPhotoSubmission(selectedQaApproval) && (() => {
                      const task = selectedQaApproval?.task;
                      const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                      const stepKeys = getRequiredSteps(selectedQaApproval);
                      const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                      
                      return (
                        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-purple-700">📷 Multi-Photo:</span>
                              <div className="flex gap-1">
                                {shouldUseStepKeyTabs ? (
                                  // StepKey-based tabs
                                  stepKeys.map((stepKey) => {
                                    const isActive = activeStepKeyQaApproval === stepKey;
                                    const displayLabel = getStepDisplayLabel(stepKey, task);
                                    
                                    return (
                                      <button
                                        key={stepKey}
                                        onClick={() => {
                                          setActiveStepKeyQaApproval(stepKey);
                                          const mediaItem = selectedQaApproval?.media?.find((m: any) => stepKeyFromMedia(m) === stepKey);
                                          setActiveRoleQaApproval(mediaItem?.role || 'primary');
                                        }}
                                        className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                          isActive
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                        }`}
                                      >
                                        {displayLabel}
                                      </button>
                                    );
                                  })
                                ) : (
                                  // Role-based tabs (legacy)
                                  getRequiredRoles(selectedQaApproval).map((role) => (
                                    <button
                                      key={role}
                                      onClick={() => {
                                        setActiveRoleQaApproval(role);
                                        const mediaItem = selectedQaApproval?.media?.find((m: any) => m.role === role);
                                        setActiveStepKeyQaApproval(stepKeyFromMedia(mediaItem));
                                      }}
                                      className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                        activeRoleQaApproval === role
                                          ? 'bg-purple-600 text-white'
                                          : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                      }`}
                                    >
                                      {getRoleDisplayLabel(role, selectedQaApproval)}
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {getImageUrlForRole(selectedQaApproval, activeRoleQaApproval) ? (
                      <div className="mb-4">
                        <img
                          src={getImageUrlForRole(selectedQaApproval, activeRoleQaApproval) || ''}
                          alt="Submission"
                          className="max-w-full rounded border"
                          style={{ maxHeight: 400 }}
                          onError={e => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder.png";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="mb-4 text-gray-400">No image available</div>
                    )}

                    {(() => {
                      const labelsToShow = getActiveLabels(selectedQaApproval, activeRoleQaApproval);
                      if (!labelsToShow || labelsToShow.length === 0) return null;
                      return (
                      <div className="mb-4 p-3 bg-gray-50 rounded">
                          <div className="font-semibold mb-2">
                            Labels ({labelsToShow.length})
                            {isMultiPhotoSubmission(selectedQaApproval) && (
                              <span className="text-xs text-gray-500 ml-2">
                                - {getRoleDisplayLabel(activeRoleQaApproval, selectedQaApproval)}
                              </span>
                            )}:
                          </div>
                        <div className="text-sm text-gray-600">
                            {labelsToShow.slice(0, 10).map((l: any) => l.categoryId || l).join(', ')}
                            {labelsToShow.length > 10 && '...'}
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-gray-500 p-8 text-center">
                    <p className="mb-2">No submission selected.</p>
                    <p className="text-sm">Select a submission from the queue on the right to review it.</p>
                    {qaApprovalSubmissions.length === 0 && (
                      <p className="text-sm mt-4 text-blue-600">
                        No submissions in QA Approvals queue. Send submissions from Task Approvals to see them here.
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="w-80 border p-4 min-h-[300px]">
                {/* Submission Details */}
                {selectedQaApproval && (
                  <div className="mb-4 pb-4 border-b">
                    {/* Sent to QA Reason section */}
                    {selectedQaApproval.qaReviewReason && (
                      <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                        <h4 className="font-semibold text-sm text-orange-900 mb-1">Sent to QA Reason</h4>
                        <p className="text-sm text-orange-800">{selectedQaApproval.qaReviewReason}</p>
                      </div>
                    )}
                    <h3 className="font-semibold mb-3">Submission Details</h3>
                    <div className="space-y-2">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700">Task:</span>
                          <span className="text-gray-900">{selectedQaApproval.task?.title || selectedQaApproval.taskTitle || '-'}</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Category:</span>
                            <span className="text-gray-900">{selectedQaApproval.category || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Subcategory:</span>
                            <span className="text-gray-900">{selectedQaApproval.subcategory || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Item Type:</span>
                            <span className="text-gray-900">{selectedQaApproval.itemType || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Language:</span>
                            <span className="text-gray-900">{selectedQaApproval.contentLanguageLabel || selectedQaApproval.contentLanguageCode || '-'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">User:</span> {selectedQaApproval.userId}
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Validation Prompt</div>
                      <div className="text-sm text-gray-600 whitespace-pre-wrap">
                        {selectedQaApproval?.validationDetails?.prompt || (selectedQaApproval as any)?.validationPrompt || selectedQaApproval?.task?.validationPrompt || "-"}
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center">
                      <button
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                        onClick={() => {
                          setSelectedQaApproval(selectedQaApproval);
                          setShowDeleteConfirm(true);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
                
                <h3 className="font-semibold mb-2">QA Approvals Queue ({qaApprovalSubmissions.length})</h3>
                <ul className="overflow-y-auto max-h-[600px]">
                  {qaApprovalSubmissions.map(p => (
                    <li
                      key={p.id}
                      className={`p-2 mb-2 rounded cursor-pointer border ${
                        selectedQaApproval?.id === p.id ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"
                      }`}
                      onClick={() => setSelectedQaApproval(p)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{p.task?.title || "Unknown Task"}</div>
                          <div className="text-xs text-gray-600">{p.userId}</div>
                          <div className="text-xs text-gray-500">
                            {p.category}{p.subcategory && ` · ${p.subcategory}`}{p.contentLanguageLabel && ` · ${p.contentLanguageLabel}`}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {qaApprovalSubTab === "saved" && (
            <div className="text-gray-500 p-8 text-center">
              <p>No submissions saved for later in QA Approvals.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "qaAutoValidation" && (
        <div className="flex gap-4">
          <div className="flex-1 border p-4 min-h-[300px]">
            {loading ? (
              <div>Loading...</div>
            ) : selectedFlagged ? (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Flagged Submission Review</h2>
                  <button 
                    className="bg-gray-500 text-white px-4 py-2 rounded flex items-center gap-2"
                    onClick={() => setSelectedFlagged(null)}
                  >
                    ← Back to QA List
                  </button>
                </div>
                <div className="mb-2">Task: {selectedFlagged.task?.title || "Unknown"}</div>
                <div className="mb-2">User: {selectedFlagged.userId}</div>
                <div className="mb-2">Type: {selectedFlagged.task?.type}</div>
                <div className="mb-2">Status: <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">Flagged</span></div>
                {getSimilarityInfo(selectedFlagged)}
                {getLabelingInfo(selectedFlagged)}
                {/* Multi-photo Role Toggle */}
                {isMultiPhotoSubmission(selectedFlagged) && (
                  <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-purple-700">📷 Multi-Photo:</span>
                      <div className="flex gap-1">
                        {getRequiredRoles(selectedFlagged).map((role) => (
                          <button
                            key={role}
                            onClick={() => setActiveRoleFlagged(role)}
                            className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                              activeRoleFlagged === role
                                ? 'bg-purple-600 text-white'
                                : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                            }`}
                          >
                            {getRoleDisplayLabel(role, selectedFlagged)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="mb-4">
                  {getImageUrlForRole(selectedFlagged, activeRoleFlagged) ? (
                    <>
                      <img
                        src={getImageUrlForRole(selectedFlagged, activeRoleFlagged) || ''}
                        alt="Submission"
                        className="mt-2 max-w-xs rounded border"
                        style={{ maxHeight: 300 }}
                        onError={e => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/placeholder.png";
                        }}
                      />
                      <div className="text-xs text-gray-500 break-all mt-1">
                        <span className="font-mono">URL: {getImageUrlForRole(selectedFlagged, activeRoleFlagged)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center mt-2">
                      <img
                        src="/placeholder.png"
                        alt="No submission"
                        className="max-w-xs rounded border opacity-50"
                        style={{ maxHeight: 150 }}
                      />
                      <span className="text-gray-400 text-xs mt-2">No image submitted</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button 
                    className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2" 
                    onClick={() => handleQAOverride("correct")}
                  >
                    ✅ Mark as Correct
                  </button>
                  <button 
                    className="bg-orange-500 text-white px-4 py-2 rounded flex items-center gap-2" 
                    onClick={() => handleQAOverride("retrain")}
                  >
                    ❌ Send to Retrain
                  </button>
                </div>
              </div>
            ) : (
              <div>No flagged submission selected.</div>
            )}
          </div>
          <div className="w-80 border p-4 min-h-[300px]">
            <h3 className="font-semibold mb-2">Flagged Queue ({flaggedSubmissions.length})</h3>
            <ul>
              {flaggedSubmissions.map(p => (
                <li
                  key={p.id}
                  className={`p-2 mb-2 rounded cursor-pointer border ${selectedFlagged?.id === p.id ? "bg-red-100 border-red-300" : "hover:bg-gray-100"}`}
                  onClick={() => setSelectedFlagged(p)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium">{p.task?.title || "Unknown"}</div>
                      <div className="text-sm text-gray-600">{p.userId}</div>
                    </div>
                    {getConfidenceBadge(p.labelingDetails?.matchConfidence || "low")}
                  </div>
                  {p.labels && (
                    <div className="text-xs text-gray-500 mt-1">
                      Labels: {p.labels.slice(0, 3).join(', ')}{p.labels.length > 3 ? '...' : ''}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {activeTab === "qaReview" && (
        <div>
          {/* Sub-tabs for QA Workbench */}
          <div className="border-b mb-4">
            <div className="flex">
              <button
                className={`px-4 py-2 font-medium ${qaReviewSubTab === "active" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setQaReviewSubTab("active")}
              >
                Active ({qaReviewSubmissions.length})
              </button>
              <button
                className={`px-4 py-2 font-medium ${qaReviewSubTab === "saved" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setQaReviewSubTab("saved")}
              >
                Save for Later ({savedForLaterSubmissions.length})
              </button>
            </div>
          </div>

          {qaReviewSubTab === "active" && (
            <div className="flex gap-4">
              <div className="flex-1 border p-4 min-h-[300px]">
                {loading ? (
                  <div>Loading...</div>
                ) : selectedQaReview ? (
                  <div>
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <button
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                        onClick={handleApproveToWorkbench}
                      >
                        Approve to Workbench
                      </button>
                      <button
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        onClick={handleApprovePhotoOnly}
                      >
                        Approve Photo Only
                      </button>
                      <button
                        className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                        onClick={() => handleEditSubmission(selectedQaReview)}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
                        onClick={handleSaveForLater}
                      >
                        Save for Later
                      </button>
                    </div>
                    
                    <h2 className="text-xl font-semibold mb-4">QA Review Submission</h2>
                    
                    {/* Multi-photo Step/Role Toggle */}
                    {isMultiPhotoSubmission(selectedQaReview) && (() => {
                      const task = selectedQaReview?.task;
                      const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                      const stepKeys = getRequiredSteps(selectedQaReview);
                      const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                      
                      return (
                        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-purple-700">📷 Multi-Photo:</span>
                            <div className="flex gap-1">
                              {shouldUseStepKeyTabs ? (
                                // StepKey-based tabs
                                stepKeys.map((stepKey) => {
                                  const isActive = activeStepKeyQaReview === stepKey;
                                  const displayLabel = getStepDisplayLabel(stepKey, task);
                                  
                                  return (
                                    <button
                                      key={stepKey}
                                      onClick={() => {
                                        setActiveStepKeyQaReview(stepKey);
                                        const mediaItem = selectedQaReview?.media?.find((m: any) => stepKeyFromMedia(m) === stepKey);
                                        setActiveRoleQaReview(mediaItem?.role || 'primary');
                                      }}
                                      className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                        isActive
                                          ? 'bg-purple-600 text-white'
                                          : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                      }`}
                                    >
                                      {displayLabel}
                                    </button>
                                  );
                                })
                              ) : (
                                // Role-based tabs (legacy)
                                getRequiredRoles(selectedQaReview).map((role) => (
                                  <button
                                    key={role}
                                    onClick={() => {
                                      setActiveRoleQaReview(role);
                                      const mediaItem = selectedQaReview?.media?.find((m: any) => m.role === role);
                                      setActiveStepKeyQaReview(stepKeyFromMedia(mediaItem));
                                    }}
                                    className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                      activeRoleQaReview === role
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                    }`}
                                  >
                                    {getRoleDisplayLabel(role, selectedQaReview)}
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {(() => {
                      // Get image URL based on stepKey or role
                      const imageUrl = (() => {
                        const task = selectedQaReview?.task;
                        const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                        const stepKeys = getRequiredSteps(selectedQaReview);
                        const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                        
                        if (shouldUseStepKeyTabs && activeStepKeyQaReview) {
                          const mediaItem = selectedQaReview?.media?.find((m: any) => stepKeyFromMedia(m) === activeStepKeyQaReview);
                          return mediaItem?.url || null;
                        }
                        return getImageUrlForRole(selectedQaReview, activeRoleQaReview);
                      })();
                      
                      return imageUrl ? (
                        <div className="mb-4">
                          <img
                            src={imageUrl}
                            alt="Submission"
                            className="max-w-full rounded border"
                            style={{ maxHeight: 400 }}
                            onError={e => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/placeholder.png";
                            }}
                          />
                        </div>
                      ) : (
                        <div className="mb-4 text-gray-400">No image available</div>
                      );
                    })()}

                    {(() => {
                      const task = selectedQaReview?.task;
                      const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                      const stepKeys = getRequiredSteps(selectedQaReview);
                      const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                      
                      const labelsToShow = shouldUseStepKeyTabs 
                        ? getLabelsForStep(selectedQaReview, activeStepKeyQaReview)
                        : getActiveLabels(selectedQaReview, activeRoleQaReview);
                      
                      if (!labelsToShow || labelsToShow.length === 0) return null;
                      
                      const displayLabel = shouldUseStepKeyTabs
                        ? getStepDisplayLabel(activeStepKeyQaReview, task)
                        : getRoleDisplayLabel(activeRoleQaReview, selectedQaReview);
                      
                      return (
                      <div className="mb-4 p-3 bg-gray-50 rounded">
                          <div className="font-semibold mb-2">
                            Labels ({labelsToShow.length})
                            {isMultiPhotoSubmission(selectedQaReview) && (
                              <span className="text-xs text-gray-500 ml-2">
                                - {displayLabel}
                              </span>
                            )}:
                          </div>
                        <div className="text-sm text-gray-600">
                            {labelsToShow.slice(0, 10).map((l: any) => l.categoryId || l).join(', ')}
                            {labelsToShow.length > 10 && '...'}
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-gray-500 p-8 text-center">
                    <p className="mb-2">No submission selected.</p>
                    <p className="text-sm">Select a submission from the queue on the right to review it.</p>
                    {qaReviewSubmissions.length === 0 && (
                      <p className="text-sm mt-4 text-blue-600">
                        No submissions in QA Workbench queue. Send submissions from Workbench to see them here.
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="w-80 border p-4 min-h-[300px]">
                {/* Submission Details */}
                {selectedQaReview && (
                  <div className="mb-4 pb-4 border-b">
                    {/* Sent to QA Reason section */}
                    {selectedQaReview.qaReviewReason && (
                      <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                        <h4 className="font-semibold text-sm text-orange-900 mb-1">Sent to QA Reason</h4>
                        <p className="text-sm text-orange-800">{selectedQaReview.qaReviewReason}</p>
                      </div>
                    )}
                    <h3 className="font-semibold mb-3">Submission Details</h3>
                    <div className="space-y-2">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700">Task:</span>
                          <span className="text-gray-900">{selectedQaReview.task?.title || selectedQaReview.taskTitle || '-'}</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Category:</span>
                            <span className="text-gray-900">{selectedQaReview.category || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Subcategory:</span>
                            <span className="text-gray-900">{selectedQaReview.subcategory || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Item Type:</span>
                            <span className="text-gray-900">{selectedQaReview.itemType || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Language:</span>
                            <span className="text-gray-900">{selectedQaReview.contentLanguageLabel || selectedQaReview.contentLanguageCode || '-'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">User:</span> {selectedQaReview.userId}
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Validation Prompt</div>
                      <div className="text-sm text-gray-600 whitespace-pre-wrap">
                        {selectedQaReview?.validationDetails?.prompt || (selectedQaReview as any)?.validationPrompt || selectedQaReview?.task?.validationPrompt || "-"}
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center">
                      <button
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                        onClick={() => {
                          setSelectedQaReview(selectedQaReview);
                          setShowDeleteConfirm(true);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
                
                <h3 className="font-semibold mb-2">QA Workbench Queue ({qaReviewSubmissions.length})</h3>
                <ul className="overflow-y-auto max-h-[600px]">
                  {qaReviewSubmissions.map(p => (
                    <li
                      key={p.id}
                      className={`p-2 mb-2 rounded cursor-pointer border ${
                        selectedQaReview?.id === p.id ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"
                      }`}
                      onClick={() => setSelectedQaReview(p)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{p.task?.title || "Unknown Task"}</div>
                          <div className="text-xs text-gray-600">{p.userId}</div>
                          <div className="text-xs text-gray-500">
                            {p.category}{p.subcategory && ` · ${p.subcategory}`}{p.contentLanguageLabel && ` · ${p.contentLanguageLabel}`}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {qaReviewSubTab === "saved" && (
            <div className="flex gap-4">
              <div className="flex-1 border p-4 min-h-[300px]">
                {loading ? (
                  <div>Loading...</div>
                ) : selectedSaved ? (
                  <div>
                    <div className="mb-4">
                      <h2 className="text-xl font-semibold mb-4">QA Workbench Submission</h2>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <button
                          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                          onClick={() => handleSendSavedToWorkbench(selectedSaved)}
                        >
                          Approve to Workbench
                        </button>
                        <button
                          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                          onClick={handleApprovePhotoOnly}
                        >
                          Approve Photo Only
                        </button>
                        <button
                          className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                          onClick={() => handleEditSubmission(selectedSaved)}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                    
                    {/* Multi-photo Step/Role Toggle */}
                    {isMultiPhotoSubmission(selectedSaved) && (() => {
                      const task = selectedSaved?.task;
                      const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                      const stepKeys = getRequiredSteps(selectedSaved);
                      const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                      
                      return (
                        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-purple-700">📷 Multi-Photo:</span>
                            <div className="flex gap-1">
                              {shouldUseStepKeyTabs ? (
                                // StepKey-based tabs
                                stepKeys.map((stepKey) => {
                                  const isActive = activeStepKeySaved === stepKey;
                                  const displayLabel = getStepDisplayLabel(stepKey, task);
                                  
                                  return (
                                    <button
                                      key={stepKey}
                                      onClick={() => {
                                        setActiveStepKeySaved(stepKey);
                                        const mediaItem = selectedSaved?.media?.find((m: any) => stepKeyFromMedia(m) === stepKey);
                                        setActiveRoleSaved(mediaItem?.role || 'primary');
                                      }}
                                      className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                        isActive
                                          ? 'bg-purple-600 text-white'
                                          : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                      }`}
                                    >
                                      {displayLabel}
                                    </button>
                                  );
                                })
                              ) : (
                                // Role-based tabs (legacy)
                                getRequiredRoles(selectedSaved).map((role) => (
                                  <button
                                    key={role}
                                    onClick={() => {
                                      setActiveRoleSaved(role);
                                      const mediaItem = selectedSaved?.media?.find((m: any) => m.role === role);
                                      setActiveStepKeySaved(stepKeyFromMedia(mediaItem));
                                    }}
                                    className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                      activeRoleSaved === role
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                    }`}
                                  >
                                    {getRoleDisplayLabel(role, selectedSaved)}
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {(() => {
                      // Get image URL based on stepKey or role
                      const imageUrl = (() => {
                        const task = selectedSaved?.task;
                        const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                        const stepKeys = getRequiredSteps(selectedSaved);
                        const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                        
                        if (shouldUseStepKeyTabs && activeStepKeySaved) {
                          const mediaItem = selectedSaved?.media?.find((m: any) => stepKeyFromMedia(m) === activeStepKeySaved);
                          return mediaItem?.url || null;
                        }
                        return getImageUrlForRole(selectedSaved, activeRoleSaved);
                      })();
                      
                      return imageUrl ? (
                        <div className="mb-4">
                          <img
                            src={imageUrl}
                            alt="Submission"
                            className="max-w-full rounded border"
                            style={{ maxHeight: 400 }}
                            onError={e => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/placeholder.png";
                            }}
                          />
                        </div>
                      ) : (
                        <div className="mb-4 text-gray-400">No image available</div>
                      );
                    })()}

                    {(() => {
                      const task = selectedSaved?.task;
                      const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                      const stepKeys = getRequiredSteps(selectedSaved);
                      const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                      
                      const labelsToShow = shouldUseStepKeyTabs 
                        ? getLabelsForStep(selectedSaved, activeStepKeySaved)
                        : getActiveLabels(selectedSaved, activeRoleSaved);
                      
                      if (!labelsToShow || labelsToShow.length === 0) return null;
                      
                      const displayLabel = shouldUseStepKeyTabs
                        ? getStepDisplayLabel(activeStepKeySaved, task)
                        : getRoleDisplayLabel(activeRoleSaved, selectedSaved);
                      
                      return (
                      <div className="mb-4 p-3 bg-gray-50 rounded">
                          <div className="font-semibold mb-2">
                            Labels ({labelsToShow.length})
                            {isMultiPhotoSubmission(selectedSaved) && (
                              <span className="text-xs text-gray-500 ml-2">
                                - {displayLabel}
                              </span>
                            )}:
                          </div>
                        <div className="text-sm text-gray-600">
                            {labelsToShow.slice(0, 10).map((l: any) => l.categoryId || l).join(', ')}
                            {labelsToShow.length > 10 && '...'}
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-gray-500 p-8 text-center">
                    <p className="mb-2">No submission selected.</p>
                    <p className="text-sm">Select a submission from the queue on the right to review it.</p>
                    {savedForLaterSubmissions.length === 0 && (
                      <p className="text-sm mt-4 text-blue-600">
                        No submissions saved for later.
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="w-80 border p-4 min-h-[300px]">
                {/* Submission Details */}
                {selectedSaved && (
                  <div className="mb-4 pb-4 border-b">
                    <h3 className="font-semibold mb-3">Submission Details</h3>
                    <div className="space-y-2">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700">Task:</span>
                          <span className="text-gray-900">{selectedSaved.task?.title || selectedSaved.taskTitle || '-'}</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Category:</span>
                            <span className="text-gray-900">{selectedSaved.category || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Subcategory:</span>
                            <span className="text-gray-900">{selectedSaved.subcategory || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Item Type:</span>
                            <span className="text-gray-900">{selectedSaved.itemType || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-700">Language:</span>
                            <span className="text-gray-900">{selectedSaved.contentLanguageLabel || selectedSaved.contentLanguageCode || '-'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">User:</span> {selectedSaved.userId}
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Validation Prompt</div>
                      <div className="text-sm text-gray-600 whitespace-pre-wrap">
                        {selectedSaved?.validationDetails?.prompt || (selectedSaved as any)?.validationPrompt || selectedSaved?.task?.validationPrompt || "-"}
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center">
                      <button
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                        onClick={() => {
                          setSelectedSaved(selectedSaved);
                          setShowDeleteConfirm(true);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
                
                <h3 className="font-semibold mb-2">Saved for Later ({savedForLaterSubmissions.length})</h3>
                <ul className="overflow-y-auto max-h-[600px]">
                  {savedForLaterSubmissions.map(p => (
                    <li
                      key={p.id}
                      className={`p-2 mb-2 rounded cursor-pointer border ${
                        selectedSaved?.id === p.id ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"
                      }`}
                      onClick={() => setSelectedSaved(p)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{p.task?.title || "Unknown Task"}</div>
                          <div className="text-xs text-gray-600">{p.userId}</div>
                          <div className="text-xs text-gray-500">
                            {p.category}{p.subcategory && ` · ${p.subcategory}`}{p.contentLanguageLabel && ` · ${p.contentLanguageLabel}`}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Submission Modal */}
      {showEditModal && editingSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Edit Submission</h2>
              <button 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSubmission(null);
                }} 
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Name</label>
                <select
                  value={editForm.taskName}
                  onChange={(e) => setEditForm({ ...editForm, taskName: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a task...</option>
                  {tasks.map(task => (
                    <option key={task.id} value={task.title}>{task.title}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter category"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                <input
                  type="text"
                  value={editForm.subcategory}
                  onChange={(e) => setEditForm({ ...editForm, subcategory: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter subcategory"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
                <input
                  type="text"
                  value={editForm.itemType}
                  onChange={(e) => setEditForm({ ...editForm, itemType: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter item type"
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSubmission(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (selectedQaReview || selectedSaved || selectedQaApproval) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-red-600">Delete Submission</h2>
              <button 
                onClick={() => setShowDeleteConfirm(false)} 
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                <strong>Are you sure you want to permanently delete this submission?</strong>
              </p>
              <p className="text-sm text-gray-600 mb-2">
                This action cannot be undone. The submission will be permanently removed from the system.
              </p>
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-sm text-red-800">
                  <strong>Task:</strong> {(selectedQaApproval || (qaReviewSubTab === "saved" ? selectedSaved : selectedQaReview))?.task?.title || "Unknown"}
                </p>
                <p className="text-sm text-red-800">
                  <strong>User:</strong> {(selectedQaApproval || (qaReviewSubTab === "saved" ? selectedSaved : selectedQaReview))?.userId}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSubmission}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Yes, Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal for QA Approvals */}
      {showRejectModal && rejectingSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Reject Submission</h2>
              <button onClick={handleQAApprovalRejectModalClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            {rejectingSubmission && (
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  <strong>Task:</strong> {rejectingSubmission.task?.title || "Unknown"}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>User:</strong> {rejectingSubmission.userId}
                </p>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Rejection Reason:
              </label>
              <select 
                value={rejectReason} 
                onChange={(e) => handleQAApprovalRejectReasonChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Choose a reason...</option>
                {rejectionReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>
            
            {rejectReason === "Other" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Reason:
                </label>
                <textarea
                  value={customRejectReason}
                  onChange={(e) => setCustomRejectReason(e.target.value)}
                  placeholder="Please provide a specific reason for rejection..."
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>
            )}
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleQAApprovalRejectModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleQAApprovalRejectSubmit}
                disabled={!rejectReason || (rejectReason === "Other" && !customRejectReason.trim())}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Reject Submission
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "qaFinalReview" && (
        <div>
          {/* Sub-tabs for QA Final Review */}
          <div className="border-b mb-4">
            <div className="flex">
              <button
                className={`px-4 py-2 font-medium ${qaFinalReviewSubTab === "active" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setQaFinalReviewSubTab("active")}
              >
                Active ({qaFinalReviewSubmissions.length})
              </button>
              <button
                className={`px-4 py-2 font-medium ${qaFinalReviewSubTab === "saved" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setQaFinalReviewSubTab("saved")}
              >
                Save for Later ({savedForLaterFinalReviewSubmissions.length})
              </button>
            </div>
          </div>

          {qaFinalReviewSubTab === "active" && (
            <div className="flex gap-4">
            <div className="flex-1 border p-4 min-h-[300px]">
              {loading ? (
                <div>Loading...</div>
              ) : selectedQaFinalReview ? (
                <div>
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                      onClick={handleApproveFinalReviewToWorkbench}
                    >
                      Send to Workbench
                    </button>
                    <button
                      className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                      onClick={() => handleEditSubmission(selectedQaFinalReview)}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                      onClick={handleApprovePhotoOnly}
                    >
                      Approve Photo Only
                    </button>
                    <button
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      onClick={handleFinalApproval}
                    >
                      Final Approval
                    </button>
                    <button
                      className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
                      onClick={handleSaveForLater}
                    >
                      Save for Later
                    </button>
                  </div>
                  
                  <h2 className="text-xl font-semibold mb-4">QA Final Review Submission</h2>
                  
                  {/* Multi-photo Step/Role Toggle */}
                  {isMultiPhotoSubmission(selectedQaFinalReview) && (() => {
                    const task = selectedQaFinalReview?.task;
                    const hasTaskSteps = task?.steps && Array.isArray(task.steps) && task.steps.length > 0;
                    const stepKeys = getRequiredSteps(selectedQaFinalReview);
                    const shouldUseStepKeyTabs = hasTaskSteps || stepKeys.length > 1;
                    
                    return (
                      <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-purple-700">📷 Multi-Photo:</span>
                          <div className="flex gap-1">
                            {shouldUseStepKeyTabs ? (
                              // StepKey-based tabs
                              stepKeys.map((stepKey) => {
                                const isActive = activeStepKeyQaFinalReview === stepKey;
                                const displayLabel = getStepDisplayLabel(stepKey, task);
                                
                                return (
                                  <button
                                    key={stepKey}
                                    onClick={() => {
                                      setActiveStepKeyQaFinalReview(stepKey);
                                      const mediaItem = selectedQaFinalReview?.media?.find((m: any) => stepKeyFromMedia(m) === stepKey);
                                      setActiveRoleQaFinalReview(mediaItem?.role || 'primary');
                                    }}
                                    className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                      isActive
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                    }`}
                                  >
                                    {displayLabel}
                                  </button>
                                );
                              })
                            ) : (
                              // Role-based tabs (legacy)
                              getRequiredRoles(selectedQaFinalReview).map((role) => (
                                <button
                                  key={role}
                                  onClick={() => {
                                    setActiveRoleQaFinalReview(role);
                                    const mediaItem = selectedQaFinalReview?.media?.find((m: any) => m.role === role);
                                    setActiveStepKeyQaFinalReview(stepKeyFromMedia(mediaItem));
                                  }}
                                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                    activeRoleQaFinalReview === role
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                  }`}
                                >
                                  {getRoleDisplayLabel(role, selectedQaFinalReview)}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {(() => {
                    // Get image URL based on stepKey or role
                    const imageUrl = (() => {
                      if (activeStepKeyQaFinalReview && activeStepKeyQaFinalReview !== 'primary') {
                        const mediaItem = selectedQaFinalReview?.media?.find((m: any) => stepKeyFromMedia(m) === activeStepKeyQaFinalReview);
                        return mediaItem?.url;
                      }
                      return getImageUrlForRole(selectedQaFinalReview, activeRoleQaFinalReview);
                    })();
                    
                    return imageUrl ? (
                      <div className="mb-4">
                        <img
                          src={imageUrl}
                          alt="Submission"
                          className="max-w-full rounded border"
                          style={{ maxHeight: 400 }}
                          onError={e => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder.png";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="mb-4 text-gray-400">No image available</div>
                    );
                  })()}

                  {/* Labels Panel */}
                  <LabelListPanel
                    labels={qaFinalReviewActiveLabels}
                    submission={selectedQaFinalReview}
                    activeRole={activeRoleQaFinalReview}
                    onFlagClick={handleFlagClickFinalReview}
                    getCategoryName={(categoryId: string) =>
                      getCategoryName(
                        categoryId,
                        qaFinalReviewSchema || (selectedQaFinalReview.task?.detection?.schemaRef ? { classes: [] } : undefined)
                      )
                    }
                    getCategoryDescription={(categoryId: string) =>
                      getCategoryById(categoryId)?.description || ''
                    }
                    variant="qa"
                    showRoleBreakdown={false}
                    hideWhenEmpty={true}
                    selectedIndex={selectedLabelIndexQaFinalReview}
                    onSelect={setSelectedLabelIndexQaFinalReview}
                  />

                  {qaFinalReviewImageAttributes.length > 0 && (
                    <div className="mb-4 p-3 bg-gray-50 rounded">
                      <h3 className="font-semibold mb-3">Image</h3>
                      <AttributePanel
                        attributes={qaFinalReviewImageAttributes}
                        values={selectedQaFinalReview.imageAttributes || {}}
                        onChange={handleQaFinalReviewImageAttributeChange}
                        compact
                      />
                    </div>
                  )}

                  {selectedQaFinalReviewLabel &&
                    selectedQaFinalReviewLabelInstanceAttributes.length > 0 && (
                      <div className="mb-4 p-3 bg-gray-50 rounded">
                        <h3 className="font-semibold mb-3">Object Attributes</h3>
                        <AttributePanel
                          attributes={selectedQaFinalReviewLabelInstanceAttributes}
                          values={selectedQaFinalReviewLabel.attributes || {}}
                          onChange={handleQaFinalReviewLabelAttributeChange}
                          flags={selectedQaFinalReviewLabel.attributeFlags || {}}
                          onFlagAttribute={handleQaFinalReviewAttributeFlagClick}
                          compact
                        />
                      </div>
                    )}
                </div>
              ) : (
                <div className="text-gray-500 p-8 text-center">
                  <p className="mb-2">No submission selected.</p>
                  <p className="text-sm">Select a submission from the queue on the right to review it.</p>
                  {qaFinalReviewSubmissions.length === 0 && (
                    <p className="text-sm mt-4 text-blue-600">
                      No submissions in QA Final Review queue. Send submissions from Review page to see them here.
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="w-80 border p-4 min-h-[300px]">
              {/* Submission Details */}
              {selectedQaFinalReview && (
                <div className="mb-4 pb-4 border-b">
                  {/* Sent to QA Reason section */}
                  {selectedQaFinalReview.qaReviewReason && (
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                      <h4 className="font-semibold text-sm text-orange-900 mb-1">Sent to QA Reason</h4>
                      <p className="text-sm text-orange-800">{selectedQaFinalReview.qaReviewReason}</p>
                    </div>
                  )}
                  <h3 className="font-semibold mb-3">Submission Details</h3>
                  <div className="space-y-2">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-700">Task:</span>
                        <span className="text-gray-900">{selectedQaFinalReview.task?.title || selectedQaFinalReview.taskTitle || '-'}</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700">Category:</span>
                          <span className="text-gray-900">{selectedQaFinalReview.category || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700">Subcategory:</span>
                          <span className="text-gray-900">{selectedQaFinalReview.subcategory || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700">Item Type:</span>
                          <span className="text-gray-900">{selectedQaFinalReview.itemType || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700">Language:</span>
                          <span className="text-gray-900">{selectedQaFinalReview.contentLanguageLabel || selectedQaFinalReview.contentLanguageCode || '-'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">User:</span> {selectedQaFinalReview.userId}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Validation Prompt</div>
                    <div className="text-sm text-gray-600 whitespace-pre-wrap">
                      {selectedQaFinalReview?.validationDetails?.prompt || (selectedQaFinalReview as any)?.validationPrompt || selectedQaFinalReview?.task?.validationPrompt || "-"}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-center">
                    <button
                      className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                      onClick={() => {
                        setSelectedQaFinalReview(selectedQaFinalReview);
                        setShowDeleteConfirm(true);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
              
              <h3 className="font-semibold mb-2">QA Final Review Queue ({qaFinalReviewSubmissions.length})</h3>
              <ul className="overflow-y-auto max-h-[600px]">
                {qaFinalReviewSubmissions.map(p => (
                  <li
                    key={p.id}
                    className={`p-2 mb-2 rounded cursor-pointer border ${
                      selectedQaFinalReview?.id === p.id ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"
                    }`}
                    onClick={() => setSelectedQaFinalReview(p)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{p.task?.title || "Unknown Task"}</div>
                        <div className="text-xs text-gray-600">{p.userId}</div>
                        <div className="text-xs text-gray-500">
                          {p.category}{p.subcategory && ` · ${p.subcategory}`}{p.contentLanguageLabel && ` · ${p.contentLanguageLabel}`}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          )}

          {qaFinalReviewSubTab === "saved" && (
            <div className="flex gap-4">
              <div className="flex-1 border p-4 min-h-[300px]">
                {loading ? (
                  <div>Loading...</div>
                ) : selectedSavedFinalReview ? (
                  <div>
                    <div className="mb-4">
                      <h2 className="text-xl font-semibold mb-4">QA Final Review Submission (Saved)</h2>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <button
                          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                          onClick={async () => {
                            if (!selectedSavedFinalReview) return;
                            try {
                              await updateDoc(doc(db, "submissions", selectedSavedFinalReview.id), {
                                qaFinalReviewStatus: null,
                                status: 'approved',
                                approvedAt: Date.now(),
                                qaFinalReviewReviewedAt: Date.now(),
                                qaFinalReviewReviewedBy: 'admin',
                                qaAction: 'approved_to_workbench'
                              });
                              const updated = savedForLaterFinalReviewSubmissions.filter(p => p.id !== selectedSavedFinalReview.id);
                              setSavedForLaterFinalReviewSubmissions(updated);
                              setSelectedSavedFinalReview(updated[0] || null);
                              alert('Submission sent back to workbench queue!');
                            } catch (error) {
                              console.error("Error sending to workbench:", error);
                              alert("Error updating submission. Please try again.");
                            }
                          }}
                        >
                          Send to Workbench
                        </button>
                        <button
                          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                          onClick={handleApprovePhotoOnly}
                        >
                          Approve Photo Only
                        </button>
                        <button
                          className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                          onClick={() => handleEditSubmission(selectedSavedFinalReview)}
                        >
                          Edit
                        </button>
                      </div>
                      
                      {/* Multi-photo Role Toggle */}
                      {isMultiPhotoSubmission(selectedSavedFinalReview) && (
                        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-purple-700">📷 Multi-Photo:</span>
                            <div className="flex gap-1">
                              {getRequiredRoles(selectedSavedFinalReview).map((role) => (
                                <button
                                  key={role}
                                  onClick={() => setActiveRoleSavedFinalReview(role)}
                                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                    activeRoleSavedFinalReview === role
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                                  }`}
                                >
                                  {getRoleDisplayLabel(role, selectedSavedFinalReview)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {getImageUrlForRole(selectedSavedFinalReview, activeRoleSavedFinalReview) ? (
                        <div className="mb-4">
                          <img
                            src={getImageUrlForRole(selectedSavedFinalReview, activeRoleSavedFinalReview) || ''}
                            alt="Submission"
                            className="max-w-full rounded border"
                            style={{ maxHeight: 400 }}
                            onError={e => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/placeholder.png";
                            }}
                          />
                        </div>
                      ) : (
                        <div className="mb-4 text-gray-400">No image available</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 p-8 text-center">
                    <p>No submissions saved for later in QA Final Review.</p>
                  </div>
                )}
              </div>
              
              <div className="w-80 border p-4 min-h-[300px]">
                {selectedSavedFinalReview && (
                  <div className="mb-4 pb-4 border-b">
                    <h3 className="font-semibold mb-3">Submission Details</h3>
                    <div className="space-y-2">
                      <div>
                        <div className="text-lg font-bold text-gray-900 mb-1">
                          {selectedSavedFinalReview.task?.title || "Unknown Task"}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">User:</span> {selectedSavedFinalReview.userId}
                      </div>
                    </div>
                  </div>
                )}
                
                <h3 className="font-semibold mb-2">Saved for Later ({savedForLaterFinalReviewSubmissions.length})</h3>
                <ul className="overflow-y-auto max-h-[600px]">
                  {savedForLaterFinalReviewSubmissions.map(p => (
                    <li
                      key={p.id}
                      className={`p-2 mb-2 rounded cursor-pointer border ${
                        selectedSavedFinalReview?.id === p.id ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"
                      }`}
                      onClick={() => setSelectedSavedFinalReview(p)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{p.task?.title || "Unknown Task"}</div>
                          <div className="text-xs text-gray-600">{p.userId}</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Flag Modal for QA Final Review */}
      {showFlagModalFinalReview && flaggingLabelFinalReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Flag Label</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Flagging:
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="flagTypeFinalReview"
                    value="redo"
                    checked={flagTypeFinalReview === 'redo'}
                    onChange={(e) => {
                      setFlagTypeFinalReview(e.target.value as any);
                      setFlagReasonFinalReview('');
                    }}
                    className="mr-2"
                  />
                  <span>Redo</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="flagTypeFinalReview"
                    value="wrong_annotation"
                    checked={flagTypeFinalReview === 'wrong_annotation'}
                    onChange={(e) => {
                      setFlagTypeFinalReview(e.target.value as any);
                      setFlagReasonFinalReview('');
                    }}
                    className="mr-2"
                  />
                  <span>Wrong Annotation</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="flagTypeFinalReview"
                    value="annotation_not_needed"
                    checked={flagTypeFinalReview === 'annotation_not_needed'}
                    onChange={(e) => {
                      setFlagTypeFinalReview(e.target.value as any);
                      setFlagReasonFinalReview('');
                    }}
                    className="mr-2"
                  />
                  <span>Annotation Not Needed</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="flagTypeFinalReview"
                    value="other"
                    checked={flagTypeFinalReview === 'other'}
                    onChange={(e) => setFlagTypeFinalReview(e.target.value as any)}
                    className="mr-2"
                  />
                  <span>Other</span>
                </label>
              </div>
            </div>
            
            {flagTypeFinalReview === 'other' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason:
                </label>
                <textarea
                  value={flagReasonFinalReview}
                  onChange={(e) => setFlagReasonFinalReview(e.target.value)}
                  placeholder="Please provide a reason..."
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  rows={3}
                />
              </div>
            )}
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowFlagModalFinalReview(false);
                  setFlaggingLabelFinalReview(null);
                  setFlagTypeFinalReview('redo');
                  setFlagReasonFinalReview('');
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFlagSubmitFinalReview}
                disabled={flagTypeFinalReview === 'other' && !flagReasonFinalReview.trim()}
                className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Flag Label
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attribute Flag Modal for QA Final Review */}
      {showAttributeFlagModalFinalReview && flaggingAttributeNameFinalReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">
              Flag Attribute: {flaggingAttributeDisplayNameFinalReview}
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Flagging:
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="attributeFlagTypeFinalReview"
                    value="wrong_value"
                    checked={attributeFlagTypeFinalReview === 'wrong_value'}
                    onChange={() => {
                      setAttributeFlagTypeFinalReview('wrong_value');
                      setAttributeFlagReasonFinalReview('');
                    }}
                    className="mr-2"
                  />
                  <span>Wrong Value</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="attributeFlagTypeFinalReview"
                    value="cannot_determine"
                    checked={attributeFlagTypeFinalReview === 'cannot_determine'}
                    onChange={() => {
                      setAttributeFlagTypeFinalReview('cannot_determine');
                      setAttributeFlagReasonFinalReview('');
                    }}
                    className="mr-2"
                  />
                  <span>Cannot Determine</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="attributeFlagTypeFinalReview"
                    value="needs_review"
                    checked={attributeFlagTypeFinalReview === 'needs_review'}
                    onChange={() => {
                      setAttributeFlagTypeFinalReview('needs_review');
                      setAttributeFlagReasonFinalReview('');
                    }}
                    className="mr-2"
                  />
                  <span>Needs Review</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="attributeFlagTypeFinalReview"
                    value="other"
                    checked={attributeFlagTypeFinalReview === 'other'}
                    onChange={() => setAttributeFlagTypeFinalReview('other')}
                    className="mr-2"
                  />
                  <span>Other</span>
                </label>
              </div>
              {attributeFlagTypeFinalReview === 'other' && (
                <textarea
                  value={attributeFlagReasonFinalReview}
                  onChange={(e) => setAttributeFlagReasonFinalReview(e.target.value)}
                  placeholder="Please provide a reason..."
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              )}
            </div>
            <div className="flex justify-between">
              {selectedQaFinalReviewLabel?.attributeFlags?.[flaggingAttributeNameFinalReview] && (
                <button
                  onClick={handleQaFinalReviewAttributeFlagRemove}
                  className="px-4 py-2 text-red-600 border border-red-300 rounded hover:bg-red-50"
                >
                  Remove Flag
                </button>
              )}
              <div className="flex justify-end space-x-2 flex-1">
                <button
                  onClick={closeQaFinalReviewAttributeFlagModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleQaFinalReviewAttributeFlagSubmit}
                  disabled={
                    attributeFlagTypeFinalReview === 'other' &&
                    !attributeFlagReasonFinalReview.trim()
                  }
                  className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Flag Attribute
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

